import { fetchWithRefresh } from './auth';
import { removeAccents, containsIgnoringAccents } from './stringUtils';
import i18n from '../i18n';
import { validateSubdomain } from './subdomainValidator.js';

// Define the API base URL based on environment
export const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Use relative URLs in production
  : 'http://localhost:5001';

// Helper function to get access token from localStorage
export const getAccessToken = () => {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    console.warn('No access token found in localStorage');
  }
  return token;
};

export const menuService = {
  // Public API
  getPublicMenu: async (subdomain, language = 'en', menuTypeId = null, month = null, year = null) => {
    let url = `${API_BASE_URL}/api/menu/${subdomain}?language=${language}`;
    
    // Add menuTypeId parameter if provided
    if (menuTypeId) {
      url += `&menuTypeId=${menuTypeId}`;
    }
    
    // Add month and year parameters if provided
    if (month !== null && year !== null) {
      url += `&month=${month}&year=${year}`;
    }
    
    console.log(`[MenuDebug] Fetching public menu with URL: ${url}`);
    
    const res = await fetch(url);
    // Even if status is 404, the API returns useful information (empty days array and customContent)
    // so we parse the JSON response instead of throwing an error
    const data = await res.json();
    
    if (!res.ok) {
      console.log(`[MenuDebug] Menu endpoint returned ${res.status} but with data:`, data);
      
      // Check for weekly view redirect (status 303)
      if (res.status === 303 && data.redirect === 'weekly') {
        console.log('[MenuDebug] School uses weekly view, redirecting to weekly endpoint');
        
        // Convert month/year to current week if they were specified
        let week = null;
        if (month !== null && year !== null) {
          // Get the first day of the given month
          const firstDayOfMonth = new Date(year, month - 1, 1);
          
          // Calculate ISO week number
          const getWeekNumber = (d) => {
            d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
            d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
            const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
            return `${d.getUTCFullYear()}-${String(weekNo).padStart(2, '0')}`;
          };
          
          week = getWeekNumber(firstDayOfMonth);
        }
        
        // Call weekly endpoint instead
        return await menuService.getPublicWeekMenu(subdomain, language, menuTypeId, week);
      }
    }
    
    return data;
  },
  
  // Get public weekly menu data
  getPublicWeekMenu: async (subdomain, language = 'en', menuTypeId = null, week = null) => {
    let url = `${API_BASE_URL}/api/menu/${subdomain}/week?language=${language}`;
    
    // Add menuTypeId parameter if provided
    if (menuTypeId) {
      url += `&menuTypeId=${menuTypeId}`;
    }
    
    // Add week parameter if provided (format: YYYY-WW)
    if (week) {
      url += `&week=${week}`;
    }
    
    console.log(`[MenuDebug] Fetching public weekly menu with URL: ${url}`);
    
    try {
      const res = await fetch(url);
      const data = await res.json();
      
      if (!res.ok) {
        console.log(`[MenuDebug] Weekly menu endpoint returned ${res.status} with data:`, data);
      }
      
      return data;
    } catch (error) {
      console.error('[MenuDebug] Error fetching weekly menu:', error);
      throw error;
    }
  },
  
  // Get available menu types with menus for the current month
  getAvailableMenuTypes: async (subdomain, language = 'en', timestamp = null) => {
    console.log(`[DEBUG-API] getAvailableMenuTypes API call for subdomain: ${subdomain}, language: ${language} at ${new Date().toISOString()}`);
    
    try {
      // Add timestamp parameter to prevent browser caching
      const cacheBuster = timestamp ? `&_t=${timestamp}` : '';
      const response = await fetch(`${API_BASE_URL}/api/available-menu-types/${subdomain}?language=${language}${cacheBuster}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch available menu types');
      }
      
      const data = await response.json();
      console.log(`[DEBUG-API] getAvailableMenuTypes response:`, data);
      return data;
    } catch (error) {
      console.error(`[DEBUG-API] getAvailableMenuTypes error:`, error);
      throw error;
    }
  },
  
  // Public endpoint to get courses for a menu type without authentication
  getPublicMenuTypeCourses: async (menuTypeId, language = 'en', timestamp = null) => {
    try {
      // Add timestamp parameter to prevent browser caching
      const cacheBuster = timestamp ? `&_=${timestamp}` : `&_=${Date.now()}`;
      const url = `${API_BASE_URL}/api/menu-type-courses/${menuTypeId}?language=${language}${cacheBuster}`;
      
      console.log(`[MenuCoursesDebug] Fetching public menu type courses from: ${url}`);
      
      const response = await fetch(url, {
        // Prevent browser from using cached responses
        cache: 'no-store'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[MenuCoursesDebug] API error (${response.status}): ${errorText}`);
        throw new Error(`Error fetching public menu type courses: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`[MenuCoursesDebug] Successfully retrieved ${data.length} courses`);
      return data;
    } catch (error) {
      console.error('[MenuCoursesDebug] Error fetching public menu type courses:', error);
      throw error;
    }
  },
  
  // Admin API (authenticated)
  getMenus: async (schoolId) => {
    // Get current language from i18n
    const currentLanguage = i18n.language || 'en';
    console.log(`[MenuTypeDebug] Fetching menus for school ${schoolId} with language: ${currentLanguage}`);
    
    try {
      // Add a cache-busting parameter to ensure we don't get stale data from browser cache
      const cacheBuster = `_t=${Date.now()}`;
      const url = `${API_BASE_URL}/api/admin/menus?schoolId=${schoolId}&language=${currentLanguage}&${cacheBuster}`;
      
      const res = await fetchWithRefresh(url);
      console.log(`[MenuTypeDebug] getMenus API response status: ${res.status}`);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error(`[MenuTypeDebug] Failed to fetch menus: ${res.status}`, errorText);
        throw new Error('Failed to fetch menus');
      }
      
      const menus = await res.json();
      console.log(`[MenuTypeDebug] Retrieved ${menus.length} menus for school ${schoolId}`);
      
      // Log detailed menu info for debugging
      if (menus.length > 0) {
        console.log('[MenuTypeDebug] First few menus data:', menus.slice(0, 3).map(m => ({
          id: m.id,
          month: m.month,
          year: m.year,
          menu_type: m.menu_type,
          menu_type_id: m.menu_type_id,
          type_of_menu_type_id: typeof m.menu_type_id
        })));
      } else {
        console.log('[MenuTypeDebug] No menus found for this school');
      }
      
      return menus;
    } catch (error) {
      console.error('[MenuTypeDebug] Error in getMenus:', error);
      // Return empty array instead of throwing to make checkExistingMenu more resilient
      return [];
    }
  },
  
  // Check if a menu exists for a specific month and menu type
  checkExistingMenu: async (schoolId, monthOrMonthNum, menuTypeIdOrYear, menuTypeId) => {
    try {
      // Determine parameter count by checking if menuTypeId is undefined
      const paramCount = menuTypeId === undefined ? 3 : 4;
      console.log(`[DEBUG] checkExistingMenu called with parameters:`, {
        schoolId, 
        monthOrMonthNum, 
        menuTypeIdOrYear,
        menuTypeId,
        paramCount
      });
      
      let monthNumber, year, finalMenuTypeId;
      
      // Handle both parameter formats:
      // Format 1: checkExistingMenu(schoolId, monthString, menuTypeId) - original format
      // Format 2: checkExistingMenu(schoolId, monthNum, yearNum, menuTypeId) - new format
      if (paramCount === 3) {
        // Original 3-parameter format: (schoolId, monthString, menuTypeId)
        const monthString = monthOrMonthNum;
        finalMenuTypeId = menuTypeIdOrYear;
        
        console.log(`[DEBUG] Using 3-parameter format: monthString="${monthString}", menuTypeId=${finalMenuTypeId}`);
        
        // Parse the date to get month and year numbers
        const date = new Date(monthString);
        if (isNaN(date.getTime())) {
          console.error(`[DEBUG] ERROR: Invalid date created from month string: ${monthString}`);
          return null;
        }
        
        monthNumber = date.getMonth() + 1; // JavaScript months are 0-based
        year = date.getFullYear();
      } else if (paramCount === 4) {
        // New 4-parameter format: (schoolId, monthNum, yearNum, menuTypeId)
        monthNumber = monthOrMonthNum;
        year = menuTypeIdOrYear;
        finalMenuTypeId = menuTypeId;
        
        console.log(`[DEBUG] Using 4-parameter format: month=${monthNumber}, year=${year}, menuTypeId=${finalMenuTypeId}`);
      } else {
        console.error(`[DEBUG] ERROR: Invalid number of parameters: ${paramCount}`);
        return null;
      }
      
      // Ensure menuTypeId is a number for the API call
      const menuTypeIdNumber = parseInt(finalMenuTypeId);
      if (isNaN(menuTypeIdNumber)) {
        console.error(`[DEBUG] ERROR: menuTypeId could not be parsed to a number: ${finalMenuTypeId}`);
        return null;
      }
      
      console.log(`[DEBUG] Final extracted components:`, {
        monthNumber,
        year,
        menuTypeIdNumber,
        isValidMonth: !isNaN(monthNumber),
        isValidYear: !isNaN(year)
      });
      
      // Construct the URL for the new efficient endpoint
      const url = `${API_BASE_URL}/api/admin/menus/check-direct?schoolId=${schoolId}&month=${monthNumber}&year=${year}&menuTypeId=${menuTypeIdNumber}`;
      
      console.log(`[DEBUG] Calling efficient check endpoint: ${url}`);
      
      const res = await fetchWithRefresh(url);
      
      console.log(`[DEBUG] Efficient check response status: ${res.status}`);
      
      if (res.ok) {
        // If status is OK (200), parse the response
        const existingMenu = await res.json();
        if (existingMenu) {
          console.log('[DEBUG] Found existing menu via check-direct:', existingMenu);
          return existingMenu;
        } else {
          console.log('[DEBUG] No existing menu found via check-direct (null response)');
          return null;
        }
      } else {
        // Handle other potential errors
        const errorText = await res.text();
        console.error(`[DEBUG] Error calling check-direct endpoint: ${res.status}`, errorText);
        throw new Error(`Failed to check menu existence: ${res.status}`);
      }
    } catch (error) {
      console.error('[DEBUG] Error in checkExistingMenu:', error);
      return null; // Return null on error (fail safe)
    }
  },
  
  getMenu: async (menuId, languageCode = 'en') => {
    console.log(`Fetching menu details for ID: ${menuId}, language: ${languageCode}`);
    try {
      // Add a cache-busting parameter to prevent browser caching
      const cacheBuster = `_t=${Date.now()}`;
      
      // Check if menuId has the format YYYY-MM-menuTypeId
      if (typeof menuId === 'string' && menuId.includes('-')) {
        const parts = menuId.split('-');
        // If it has exactly 2 dashes (3 parts), assume it's YYYY-MM-MenuTypeId
        if (parts.length === 3) {
          console.log(`Detected composite ID format: ${menuId}. Resolving to numeric ID first.`);
          const [year, month, menuTypeId] = parts;
          
          // We need the schoolId for the API call
          // First, try to get the current school ID from localStorage
          const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
          let schoolId = currentUser.current_school?.id;
          
          // If not available, try to get it from the URL
          if (!schoolId) {
            // Check if we can get the school from another source
            console.log('No schoolId found in user data, checking other sources...');
            try {
              // Make a call to get the current user's profile which should have the school ID
              const userRes = await fetchWithRefresh(`${API_BASE_URL}/api/me`);
              if (userRes.ok) {
                const userData = await userRes.json();
                schoolId = userData.current_school?.id;
                console.log(`Got schoolId ${schoolId} from user profile`);
              }
            } catch (err) {
              console.error('Error getting user profile:', err);
            }
          }
          
          if (!schoolId) {
            console.error('Could not determine schoolId for menu lookup');
            throw new Error('Missing schoolId for menu lookup');
          }
          
          // Get all menus for the school with cache-busting parameter
          console.log(`Finding numeric ID for composite ID ${menuId} in school ${schoolId}`);
          const allMenusRes = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menus?schoolId=${schoolId}&${cacheBuster}`);
          if (!allMenusRes.ok) {
            throw new Error('Failed to fetch menus list');
          }
          
          const allMenus = await allMenusRes.json();
          console.log(`Got ${allMenus.length} menus for school ${schoolId}`);
          
          // Find the menu with matching criteria
          const monthNum = parseInt(month, 10);
          const yearNum = parseInt(year, 10);
          const menuTypeIdNum = parseInt(menuTypeId, 10);
          
          const matchingMenu = allMenus.find(menu => {
            const menuMonth = parseInt(menu.month, 10);
            const menuYear = parseInt(menu.year, 10);
            const menuTypeId = parseInt(menu.menu_type_id, 10);
            
            console.log(`Comparing: month=${menuMonth}/${monthNum}, year=${menuYear}/${yearNum}, typeId=${menuTypeId}/${menuTypeIdNum}`);
            
            return menuMonth === monthNum && 
                   menuYear === yearNum && 
                   menuTypeId === menuTypeIdNum;
          });
          
          if (!matchingMenu) {
            console.log('No matching menu found for composite ID');
            throw new Error('Menu not found');
          }
          
          // Use the matching menu's numeric ID
          console.log(`Found matching menu with ID: ${matchingMenu.id}`);
          menuId = matchingMenu.id;
        }
      }
      
      // Now proceed with the standard call using the resolved ID
      const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menus/${menuId}?languageCode=${languageCode}&${cacheBuster}`);
      if (!res.ok) {
        const errorText = await res.text();
        console.error(`Failed to fetch menu details: ${res.status}`, errorText);
        throw new Error('Failed to fetch menu details');
      }
      const data = await res.json();
      console.log('Menu data received:', data);
      return data;
    } catch (error) {
      console.error('Error in getMenu:', error);
      throw error;
    }
  },
  
  createMenu: async (data) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menus`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create menu');
    return res.json();
  },
  
  updateMenu: async (menuId, data) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menus/${menuId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update menu');
    return res.json();
  },
  
  deleteMenu: async (menuId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menus/${menuId}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete menu');
    return res.json();
  },
  
  updateDay: async (dayId, data) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/days/${dayId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update day');
    return res.json();
  },
  
  saveMenuItem: async (data) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-items`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update menu item');
    return res.json();
  },
  
  getMenuItemsForDay: async (dayId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-items/by-day/${dayId}`);
    if (!res.ok) throw new Error('Failed to fetch menu items for day');
    return res.json();
  },
  
  deleteMenuItem: async (dayId, courseId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-items`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ dayId, courseId })
    });
    if (!res.ok) throw new Error('Failed to delete menu item');
    return res.json();
  },
  
  // Add missing functions for React Query hooks
  setMenuItemDish: async (menuId, dayId, itemId, dishId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-items/${itemId}/dish`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        menuId, 
        dayId, 
        dishId 
      })
    });
    if (!res.ok) throw new Error('Failed to set menu item dish');
    return res.json();
  },
  
  clearMenuItemDish: async (menuId, dayId, itemId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-items/${itemId}/dish`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        menuId, 
        dayId 
      })
    });
    if (!res.ok) throw new Error('Failed to clear menu item dish');
    return res.json();
  },
  
  // Get available courses that can be added to a menu type
  getAvailableCoursesForMenuType: async (menuTypeId, language = 'en', timestamp = null) => {
    try {
      const token = getAccessToken();
      // Add timestamp parameter to prevent browser caching
      const cacheBuster = timestamp ? `&_=${timestamp}` : `&_=${Date.now()}`;
      const url = `${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/available-courses?language=${language}${cacheBuster}`;
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        // Prevent browser from using cached responses
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Error fetching available courses for menu type: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching available courses for menu type:', error);
      throw error;
    }
  },
  
  // Add addCourseToMenuType to menuService object
  addCourseToMenuType: async (menuTypeId, courseId) => {
    console.log(`API: Adding course ${courseId} to menu type ${menuTypeId}`);
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/courses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ courseId })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to add course to menu type');
    }
    return res.json();
  },
  
  // Add removeCourseFromMenuType to menuService object
  removeCourseFromMenuType: async (menuTypeId, courseId) => {
    console.log(`API: Removing course ${courseId} from menu type ${menuTypeId}`);
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/courses/${courseId}`, {
      method: 'DELETE'
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to remove course from menu type');
    }
    return res.json();
  },
  
  // Add reorderMenuTypeCourse to menuService object
  reorderMenuTypeCourse: async (menuTypeId, courseId, direction) => {
    console.log(`API: Reordering course ${courseId} in menu type ${menuTypeId}, direction: ${direction}`);
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/courses/${courseId}/reorder`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ direction })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to reorder course');
    }
    return res.json();
  },
  
  // Add getCourses function to menuService object
  getCourses: async (schoolId, language = 'en', timestamp = null) => {
    try {
      const token = getAccessToken();
      // Add timestamp parameter to prevent browser caching
      const cacheBuster = timestamp ? `&_=${timestamp}` : `&_=${Date.now()}`;
      const url = `${API_BASE_URL}/api/admin/courses?schoolId=${schoolId}&language=${language}${cacheBuster}`;
      
      console.log(`[CoursesDebug] Fetching courses from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        // Prevent browser from using cached responses
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Error fetching courses: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching courses:', error);
      throw error;
    }
  },
  
  // Add getCourseTranslations to menuService object
  getCourseTranslations: async (courseId) => {
    try {
      const token = getAccessToken();
      // Add timestamp parameter to prevent browser caching
      const timestamp = Date.now();
      const url = `${API_BASE_URL}/api/admin/courses/${courseId}/translations?_=${timestamp}`;
      
      console.log(`[CourseDebug] Fetching course translations from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        // Prevent browser from using cached responses
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Error fetching course translations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching course translations:', error);
      throw error;
    }
  },
  
  // Add createCourse to menuService object
  createCourse: async (schoolId, name, translations = {}) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/courses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ schoolId, name, translations }),
      });

      if (!response.ok) {
        throw new Error(`Error creating course: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating course:', error);
      throw error;
    }
  },
  
  // Add updateCourse to menuService object
  updateCourse: async (id, data) => {
    console.log(`API call: updateCourse(${id})`, data);
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/courses/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error updating course: ${response.status}`, errorText);
        throw new Error(`Error updating course: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating course:', error);
      throw error;
    }
  },
  
  // Add deleteCourse to menuService object
  deleteCourse: async (id) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/courses/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error deleting course: ${response.statusText}`);
      }

      // Handle 204 No Content response (successful deletion)
      if (response.status === 204) {
        return { success: true };
      }

      // Only try to parse JSON if there's content
      const text = await response.text();
      if (text) {
        return JSON.parse(text);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting course:', error);
      throw error;
    }
  },
  
  // Get all schools the current user has access to
  getUserSchools: async (userId) => {
    try {
      console.log('[API] Fetching user schools', userId ? `for user ${userId}` : 'for current user');
      
      // Add cache busting to prevent stale data
      const timestamp = Date.now();
      const url = `${API_BASE_URL}/api/user/schools?_t=${timestamp}`;
      
      const token = getAccessToken();
      if (!token) {
        console.error('[API] No access token available for getUserSchools');
        return [];
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });
      
      console.log(`[API] getUserSchools response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[API] Failed to fetch user schools: ${response.status}`, errorText);
        return [];
      }
      
      const schools = await response.json();
      console.log(`[API] Retrieved ${schools.length} schools for user`);
      
      // Log detailed school data for debugging
      if (schools.length > 0) {
        // Create a more detailed log for each school
        schools.forEach(school => {
          console.log(`[API] School: ${school.name} (ID: ${school.id}, subdomain: ${school.subdomain})`);
          console.log(`[API] Roles for ${school.name}:`, school.roles);
        });
        
        console.log('[API] User schools summary:', schools.map(s => ({
          id: s.id,
          name: s.name,
          subdomain: s.subdomain,
          roles: s.roles
        })));
      } else {
        console.log('[API] No schools found for this user');
      }
      
      return schools;
    } catch (error) {
      console.error('[API] Error in getUserSchools:', error);
      return [];
    }
  },
  
  // Admin API endpoint for menu type courses (authenticated)
  getMenuTypeCourses: async (menuTypeId, language = 'en', timestamp = null) => {
    try {
      const token = getAccessToken();
      // Add timestamp parameter to prevent browser caching
      const cacheBuster = timestamp ? `&_=${timestamp}` : `&_=${Date.now()}`;
      const url = `${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/courses?language=${language}${cacheBuster}`;
      
      console.log(`[MenuCoursesDebug] Fetching menu type courses from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        // Prevent browser from using cached responses
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Error fetching menu type courses: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching menu type courses:', error);
      throw error;
    }
  },
  
  // --- Notes --- 
  upsertNote: async (dayId, text) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/days/${dayId}/notes`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ text })
    });
    if (!res.ok) {
       const errorData = await res.json().catch(() => ({ message: 'Failed to upsert note' }));
       throw new Error(errorData.message || 'Failed to upsert note');
    }
    return res.json();
  },

  // --- Comments --- 
  getComments: async (dayId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/days/${dayId}/comments`);
     if (!res.ok) {
       const errorData = await res.json().catch(() => ({ message: 'Failed to fetch comments' }));
       throw new Error(errorData.message || 'Failed to fetch comments');
    }
    return res.json();
  },

  addComment: async (dayId, text) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/days/${dayId}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ text })
    });
     if (!res.ok) {
       const errorData = await res.json().catch(() => ({ message: 'Failed to add comment' }));
       throw new Error(errorData.message || 'Failed to add comment');
    }
    return res.json();
  },

  editComment: async (dayId, commentId, text) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/days/${dayId}/comments/${commentId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ text })
    });
     if (!res.ok) {
       const errorData = await res.json().catch(() => ({ message: 'Failed to edit comment' }));
       throw new Error(errorData.message || 'Failed to edit comment');
    }
    return res.json();
  },

  // Add these methods to menuService if they don't exist

  /**
   * Fetch a note for a specific day
   * @param {number} dayId - The ID of the day to fetch note for
   * @returns {Promise<object>} The note object
   */
  getNote: async (dayId) => {
    console.log(`Fetching note for day ${dayId}`);
    try {
      // Use fetchWithRefresh instead of api.get
      const response = await fetchWithRefresh(`${API_BASE_URL}/api/days/${dayId}/notes`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch note' }));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      return await response.json(); // Parse response JSON
    } catch (error) {
      console.error(`Error fetching note for day ${dayId}:`, error);
      throw error;
    }
  },

  /**
   * Mark all comments for a day as read
   * @param {number} dayId - The ID of the day to mark comments as read
   * @returns {Promise<object>} The result of the operation
   */
  markCommentsAsRead: async (dayId) => {
    console.log(`Marking comments as read for day ${dayId}`);
    try {
      // Use fetchWithRefresh instead of api.post
      const response = await fetchWithRefresh(`${API_BASE_URL}/api/days/${dayId}/comments/read`, {
        method: 'POST', // Specify the method
        headers: { // Include necessary headers if needed, e.g., Content-Type
          'Content-Type': 'application/json'
        },
        // Add body if required by the endpoint (empty for this one)
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        // Handle non-OK responses
        const errorData = await response.json().catch(() => ({ message: 'Failed to parse error response' }));
        console.error(`Error marking comments as read for day ${dayId}:`, response.status, errorData);
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }
      
      return await response.json(); // Parse JSON response for successful requests
    } catch (error) {
      // Log and re-throw the specific error
      console.error(`Error in markCommentsAsRead API call for day ${dayId}:`, error);
      throw error;
    }
  },

  // Copy a week from one menu to another
  copyWeek: async (data) => {
    console.log('[API:copyWeek] Starting copyWeek API call with data:', JSON.stringify(data, null, 2));
    try {
      console.log('[API:copyWeek] Sending POST request to /api/admin/menus/copy-week');
      const response = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menus/copy-week`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      console.log('[API:copyWeek] Response status:', response.status);
      const result = await response.json();
      console.log('[API:copyWeek] Response JSON parsing complete');
      
      if (!response.ok) {
        console.error('[API:copyWeek] Error response:', result);
        throw new Error(result.error || 'Failed to copy week');
      }
      
      console.log('[API:copyWeek] Success response. Menu ID:', result.menuId);
      
      // Make sure we have a consistent structure to return
      let menuData = result.menu;
      
      // If there's no menu property but we have days directly
      if (!menuData && Array.isArray(result.days)) {
        console.log('[API:copyWeek] Creating menu structure for days array');
        menuData = {
          id: result.menuId,
          days: result.days
        };
      }
      
      // If we still don't have valid menu data, return the whole result
      if (!menuData) {
        console.log('[API:copyWeek] Using entire result object as menu data');
        menuData = result;
      }
      
      console.log('[API:copyWeek] Menu data structure:', menuData ? {
        id: menuData.id,
        days_count: menuData.days ? menuData.days.length : 0,
        day_keys: menuData.days && menuData.days.length > 0 ? Object.keys(menuData.days[0]) : []
      } : 'No menu data');
      
      // Return the menu ID and processed menu data
      return {
        menuId: result.menuId,
        menu: menuData
      };
    } catch (error) {
      console.error('[API:copyWeek] Error during API call:', error);
      throw error;
    }
  },
  
  // Copy a single day from one menu to another
  copyDay: async (data) => {
    console.log('[API:copyDay] Starting copyDay API call with data:', JSON.stringify(data, null, 2));
    try {
      console.log('[API:copyDay] Sending POST request to /api/admin/menus/copy-day');
      const response = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menus/copy-day`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      console.log('[API:copyDay] Response status:', response.status);
      const result = await response.json();
      console.log('[API:copyDay] Response JSON parsing complete');
      
      if (!response.ok) {
        console.error('[API:copyDay] Error response:', result);
        throw new Error(result.error || 'Failed to copy day');
      }
      
      console.log('[API:copyDay] Success response. Menu ID:', result.menuId);
      
      // Make sure we have a consistent structure to return
      let menuData = result.menu;
      
      // If there's no menu property but we have days directly
      if (!menuData && Array.isArray(result.days)) {
        console.log('[API:copyDay] Creating menu structure for days array');
        menuData = {
          id: result.menuId,
          days: result.days
        };
      }
      
      // If we still don't have valid menu data, return the whole result
      if (!menuData) {
        console.log('[API:copyDay] Using entire result object as menu data');
        menuData = result;
      }
      
      console.log('[API:copyDay] Menu data structure:', menuData ? {
        id: menuData.id,
        days_count: menuData.days ? menuData.days.length : 0,
        day_keys: menuData.days && menuData.days.length > 0 ? Object.keys(menuData.days[0]) : []
      } : 'No menu data');
      
      // Return the menu ID and processed menu data
      return {
        menuId: result.menuId,
        menu: menuData
      };
    } catch (error) {
      console.error('[API:copyDay] Error during API call:', error);
      throw error;
    }
  },

  // NEW: Bulk update multiple menu items to prevent deadlocks
  bulkUpdateMenuItems: async (updates) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-items/bulk`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ updates })
    });
    if (!res.ok) throw new Error('Failed to bulk update menu items');
    return res.json();
  },

  // Get recent menus for admin dashboard (limit 5)
  getRecentMenus: async (schoolId) => {
    try {
      const response = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menus/recent?schoolId=${schoolId}&language=${i18n.language}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch recent menus: ${response.status}`);
      }
      const data = await response.json();
      console.log(`[API] Retrieved ${data.length} recent menus for school ${schoolId}`);
      return data;
    } catch (error) {
      console.error('[API] Error fetching recent menus:', error);
      throw error;
    }
  },
};

export const dishService = {
  getDishes: async (schoolId, language = 'en') => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/dishes?schoolId=${schoolId}&language=${language}&isSpecialName=false`);
    if (!res.ok) throw new Error('Failed to fetch dishes');
    return res.json();
  },
  
  searchDishes: async (schoolId, query, language = 'en') => {
    console.log(`API: Searching dishes with schoolId=${schoolId}, query=${query}, language=${language}`);
    try {
      // Get a normalized version of the query (without accents)
      // eslint-disable-next-line no-unused-vars
      const normalizedQuery = removeAccents(query);
      
      // Use a shorter query to get more matches from backend
      // If the query has accents, we also search for the same query WITHOUT accents
      // This way we get the broadest possible set of matches to filter client-side
      const searchQuery = query.length > 2 ? query.substring(0, 2) : (query || '');
      
      const url = `${API_BASE_URL}/api/admin/search-dishes?schoolId=${schoolId}&language=${language}&query=${encodeURIComponent(searchQuery)}&isSpecialName=false`;
      console.log('API: Dish search URL:', url);
      
      const res = await fetchWithRefresh(url);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('API: Search dishes failed:', res.status, errorText);
        console.warn('Returning empty array due to search error');
        return []; // Return empty array instead of throwing
      }
      
      const data = await res.json();
      console.log('API: Search dishes results count:', data.length);
      
      // Ensure data is valid
      if (!Array.isArray(data)) {
        console.error('API: Dish search results is not an array, returning empty array');
        return [];
      }
      
      // If original query is empty, return all results
      if (!query.trim()) {
        return data;
      }
      
      // Filter results client-side to match the query (accent-insensitive)
      const filteredResults = data.filter(item => {
        // Get item name (might be in different languages)
        const itemName = item.name || '';
        const allTranslations = item.all_translations || [];
        
        // Check if the name matches our query (accent-insensitive)
        if (containsIgnoringAccents(itemName, query)) {
          return true;
        }
        
        // Also check all translations
        return allTranslations.some(translation => 
          translation.name && containsIgnoringAccents(translation.name, query)
        );
      });
      
      console.log('API: Client-side filtered results count:', filteredResults.length);
      return filteredResults;
    } catch (error) {
      console.error('API: Error in searchDishes:', error);
      return []; // Return empty array instead of throwing
    }
  },
  
  getMissingTranslations: async (schoolId, selectedLanguageCodes = null) => {
    try {
      const params = new URLSearchParams();
      if (schoolId) {
        params.append('schoolId', schoolId.toString());
      }
      // Add the new optional parameter
      if (selectedLanguageCodes) {
        params.append('selectedLanguageCodes', selectedLanguageCodes);
      }
      
      const url = `${API_BASE_URL}/api/admin/missing-translations?${params.toString()}`;
      console.log(`[API] Fetching missing translations from: ${url}`); // Log the final URL
      
      const response = await fetchWithRefresh(url);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch missing translations: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching missing translations:', error);
      throw error;
    }
  },
  
  getDish: async (dishId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/dishes/${dishId}`);
    if (!res.ok) throw new Error('Failed to fetch dish details');
    return res.json();
  },
  
  createDish: async (data) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/dishes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error(`Error creating dish: ${res.status}`, errorText);
      // Try to parse JSON for better error message
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(JSON.stringify(errorJson)); // Re-throw with JSON details
      } catch (parseError) {
        throw new Error(`Error ${res.status}: ${errorText}`);
      }
    }
    return res.json();
  },
  
  updateDish: async (id, data, selectedLanguageCodes = '', userLanguage = 'en') => {
    console.log(`Updating dish ${id} with data:`, data, `selectedLangs: ${selectedLanguageCodes}, userLang: ${userLanguage}`);
    try {
      const url = `${API_BASE_URL}/api/admin/dishes/${id}?selectedLanguageCodes=${encodeURIComponent(selectedLanguageCodes)}&userLanguage=${encodeURIComponent(userLanguage)}`;
      const res = await fetchWithRefresh(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error(`Error updating dish ${id}: ${res.status}`, errorText);
        // Try to parse JSON for better error message
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(JSON.stringify(errorJson)); // Re-throw with JSON details
        } catch (parseError) {
          throw new Error(`Error ${res.status}: ${errorText}`);
        }
      }
      
      const responseData = await res.json();
      console.log(`Dish ${id} updated successfully. Response:`, responseData);
      return responseData; // Return the full response including updatedMissingItems
    } catch (err) {
      console.error(`Error in updateDish API call for ID ${id}:`, err);
      throw err;
    }
  },
  
  checkDishUsage: async (dishId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/dishes/${dishId}/check-usage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });
    
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to check dish usage');
    }
    
    return res.json();
  },
  
  replaceDishAndDelete: async (dishId, replaceWithId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/dishes/${dishId}/check-usage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ replaceWithId })
    });
    
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to replace and delete dish');
    }
    
    return res.json();
  },
  
  deleteDish: async (dishId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/dishes/${dishId}`, {
      method: 'DELETE'
    });
    
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to delete dish');
    }
    
    return res.json();
  },
  
  saveTranslations: async (data) => {
    try {
      // Assuming the item ID is in data.itemId and translations are in data.translations
      // We also need schoolId, selectedLanguageCodes, and userLanguage for the refetch
      const { 
        itemId, 
        itemType, 
        translations, 
        // Add these new parameters
        schoolId, 
        selectedLanguageCodes, 
        userLanguage 
      } = data;
      
      if (!itemId) throw new Error('Item ID is required to save translations');
      if (!translations) throw new Error('Translations data is required');
      if (!schoolId) throw new Error('School ID is required for completion check');
      if (!selectedLanguageCodes) throw new Error('Selected languages are required for completion check');
      if (!userLanguage) throw new Error('User language is required for completion check');
      
      console.log(`[API] Saving translations for ${itemType} ID: ${itemId}`);
      
      // Convert translations object into the array format the backend expects
      const formattedTranslations = Object.entries(translations).map(([code, name]) => ({
        language_code: code,
        name: name || ''
      }));
      
      // Construct URL with query parameters for the completion check
      const params = new URLSearchParams();
      params.append('selectedLanguageCodes', selectedLanguageCodes); // Should be a comma-separated string
      params.append('userLanguage', userLanguage);
      
      const url = `${API_BASE_URL}/api/admin/dishes/${itemId}?${params.toString()}`;
      const payload = { 
        translations: formattedTranslations 
      };
      
      console.log(`[API] Sending PUT to: ${url}`); // Log the full URL
      
      const response = await fetchWithRefresh(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[API Save Error]', errorData);
        throw new Error(errorData.error || `Failed to save translations: ${response.status} ${response.statusText}`);
      }
      
      // The response now includes completion status instead of full queue refetch
      const result = await response.json();
      console.log('[API] Translation save response:', result);
      
      return result; 
    } catch (error) {
      console.error('Error saving translations:', error);
      throw error;
    }
  },
  
  // REMOVED: Nutrient values are now included in the main getUnifiedItems response
  /*
  getDishNutrientValues: async (dishId) => {
    if (!dishId) {
      console.warn('[API] getDishNutrientValues called without dishId');
      return {}; // Return empty object if no dishId
    }
    try {
      // Fetch the full dish details using the existing getDish function
      const dishData = await dishService.getDish(dishId);
      
      // Extract nutrient values, assuming they are nested under a specific key
      // Adjust the key ('nutrientValues') if the backend uses a different one
      const nutrientValues = dishData?.nutrientValues || {}; 
      
      console.log(`[API] Extracted nutrient values for dish ${dishId}:`, nutrientValues);
      // Expected format: { nutrientId1: value1, nutrientId2: value2, ... }
      return nutrientValues;
    } catch (error) {
      console.error(`[API] Error fetching or extracting nutrient values for dish ${dishId}:`, error);
      // Don't throw, return empty object so UI doesn't break
      return {}; 
    }
  },
  */
  
  getDishNutrients: async (dishId) => {
    console.log(`[API] Fetching nutrients for dish ID: ${dishId}`);
    if (!dishId) {
      console.warn('[API] getDishNutrients called without dishId');
      return { nutrients: [] }; // Return empty nutrients if no dishId
    }
    
    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const url = `${API_BASE_URL}/api/admin/dishes/${dishId}/nutrients?_t=${timestamp}`;
      
      const response = await fetchWithRefresh(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[API] Error fetching nutrient values for dish ${dishId} (${response.status}): ${errorText}`);
        throw new Error(`Failed to fetch nutrient values: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`[API] Successfully fetched nutrient values for dish ${dishId}:`, data);
      return data;
    } catch (error) {
      console.error(`[API] Error in getDishNutrients for dish ${dishId}:`, error);
      throw error;
    }
  },
};

export const menuTypeService = {
  getMenuTypes: async (schoolId, language = 'en') => {
    try {
      // Make sure language parameter is valid
      const actualLanguage = language || i18n.language || 'en';
      
      // Get auth token
      const token = getAccessToken();
      const url = `${API_BASE_URL}/api/admin/menu-types?schoolId=${schoolId}&language=${actualLanguage}`;
      
      console.log('[MenuTypeDebug] Explicitly calling menu types API with URL:', url);
      console.log('[MenuTypeDebug] API call details:', { 
        schoolId, 
        requestedLanguage: language,
        actualLanguage,
        currentI18nLanguage: i18n.language,
        tokenExists: !!token
      });
      
      // Make explicit fetch request with proper error handling
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      console.log(`[MenuTypeDebug] Menu types API response status: ${response.status}`);
      
      // Special handling for 403 errors
      if (response.status === 403) {
        console.warn('[MenuTypeDebug] Menu types API returned 403, returning empty array');
        return [];
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[MenuTypeDebug] Menu types API error (${response.status}):`, errorText);
        throw new Error(`Error fetching menu types: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`[MenuTypeDebug] Menu types API success! Received ${data?.length || 0} items:`);
      console.log('[MenuTypeDebug] Menu types received:', 
        data?.map(item => ({
          id: item.id,
          name: item.name,
          translated_name: item.translated_name
        }))
      );
      return data || [];
    } catch (error) {
      console.error('[MenuTypeDebug] Error in getMenuTypes:', error);
      // Return empty array instead of throwing to prevent breaking the UI
      return [];
    }
  },
  
  createMenuType: async (schoolId, translations = {}, is_visible = true, show_nutrients = true, show_tags = true) => {
    try {
      const token = getAccessToken();
      console.log('API: Creating menu type with:', { schoolId, translations, is_visible, show_nutrients, show_tags });
      const response = await fetch(`${API_BASE_URL}/api/admin/menu-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ schoolId, translations, is_visible, show_nutrients, show_tags }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response from server:', errorText);
        throw new Error(`Error creating menu type: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating menu type:', error);
      throw error;
    }
  },
  
  updateMenuType: async (menuTypeId, translations = {}, is_visible, show_nutrients, show_tags) => {
    const payload = { translations };
    
    // Only include is_visible if it's explicitly provided (could be false)
    if (is_visible !== undefined) {
      payload.is_visible = is_visible;
    }
    
    // Only include show_nutrients if it's explicitly provided (could be false)
    if (show_nutrients !== undefined) {
      payload.show_nutrients = show_nutrients;
    }
    
    // Only include show_tags if it's explicitly provided (could be false)
    if (show_tags !== undefined) {
      payload.show_tags = show_tags;
    }
    
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error('Failed to update menu type');
    return res.json();
  },
  
  deleteMenuType: async (menuTypeId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete menu type');
    return res.json();
  },
  
  getMenuTypeTranslations: async (menuTypeId) => {
    try {
      const token = getAccessToken();
      // Add timestamp parameter to prevent browser caching
      const timestamp = Date.now();
      const url = `${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/translations?_=${timestamp}`;
      
      console.log(`[MenuTypeDebug] Fetching menu type translations from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        // Prevent browser from using cached responses
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Error fetching menu type translations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching menu type translations:', error);
      throw error;
    }
  },
  
  updateMenuTypeTranslations: async (menuTypeId, translations) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/translations`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ translations }),
      });

      if (!response.ok) {
        throw new Error(`Error updating menu type translations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating menu type translations:', error);
      throw error;
    }
  }
};

export const specialNameService = {
  getSpecialNames: async (schoolId, language = 'en') => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/dishes?schoolId=${schoolId}&language=${language}&isSpecialName=true`);
    if (!res.ok) throw new Error('Failed to fetch special names');
    return res.json();
  },
  
  searchSpecialNames: async (schoolId, query, language = 'en') => {
    console.log(`API: Searching special names with schoolId=${schoolId}, query=${query}, language=${language}`);
    try {
      // Get a normalized version of the query (without accents)
      // eslint-disable-next-line no-unused-vars
      const normalizedQuery = removeAccents(query);
      
      // Use a shorter query to get more matches from backend
      // If the query has accents, we also search for the same query WITHOUT accents
      // This way we get the broadest possible set of matches to filter client-side
      const searchQuery = query.length > 2 ? query.substring(0, 2) : (query || '');
      
      // Use the dish search endpoint with isSpecialName parameter
      const url = `${API_BASE_URL}/api/admin/search-dishes?schoolId=${schoolId}&language=${language}&query=${encodeURIComponent(searchQuery)}&isSpecialName=true`;
      console.log('API: Special names search URL:', url);
      
      const res = await fetchWithRefresh(url);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('API: Search special names failed:', res.status, errorText);
        console.warn('Returning empty array due to search error');
        return []; // Return empty array instead of throwing
      }
      
      const data = await res.json();
      console.log('API: Search special names results count:', data.length);
      
      // Ensure data is valid
      if (!Array.isArray(data)) {
        console.error('API: Special name search results is not an array, returning empty array');
        return [];
      }
      
      // If original query is empty, return all results
      if (!query.trim()) {
        return data;
      }
      
      // Filter results client-side to match the query (accent-insensitive)
      const filteredResults = data.filter(item => {
        // Get item name (might be in different languages)
        const itemName = item.name || '';
        const allTranslations = item.all_translations || [];
        
        // Check if the name matches our query (accent-insensitive)
        if (containsIgnoringAccents(itemName, query)) {
          return true;
        }
        
        // Also check all translations
        return allTranslations.some(translation => 
          translation.name && containsIgnoringAccents(translation.name, query)
        );
      });
      
      console.log('API: Client-side filtered results count:', filteredResults.length);
      return filteredResults;
    } catch (error) {
      console.error('API: Error in searchSpecialNames:', error);
      console.warn('Returning empty array due to exception');
      return []; // Return empty array instead of throwing
    }
  },
  
  getSpecialName: async (specialNameId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/dishes/${specialNameId}`);
    if (!res.ok) throw new Error('Failed to fetch special name details');
    return res.json();
  },
  
  createSpecialName: async (data) => {
    const payload = {
      ...data,
      isSpecialName: true
    };
    
    return dishService.createDish(payload);
  },
  
  updateSpecialName: async (id, data, selectedLanguageCodes = '', userLanguage = 'en') => {
    console.log(`Updating special name ${id} with data:`, data, `selectedLangs: ${selectedLanguageCodes}, userLang: ${userLanguage}`);
    try {
      // Construct URL with query parameters for completion check
      const params = new URLSearchParams();
      if (selectedLanguageCodes) {
        params.append('selectedLanguageCodes', selectedLanguageCodes);
      }
      if (userLanguage) {
        params.append('userLanguage', userLanguage);
      }
      
      const url = `${API_BASE_URL}/api/admin/special-names/${id}?${params.toString()}`;
      const res = await fetchWithRefresh(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error(`Error updating special name ${id}: ${res.status}`, errorText);
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(JSON.stringify(errorJson));
        } catch (parseError) {
          throw new Error(`Error ${res.status}: ${errorText}`);
        }
      }
      
      const responseData = await res.json();
      console.log(`Special name ${id} updated successfully. Response:`, responseData);
      return responseData;
    } catch (err) {
      console.error(`Error in updateSpecialName API call for ID ${id}:`, err);
      throw err;
    }
  },
  
  checkSpecialNameUsage: async (specialNameId) => {
    return dishService.checkDishUsage(specialNameId);
  },
  
  replaceSpecialNameAndDelete: async (specialNameId, replaceWithId) => {
    return dishService.replaceDishAndDelete(specialNameId, replaceWithId);
  },
  
  deleteSpecialName: async (specialNameId) => {
    return dishService.deleteDish(specialNameId);
  }
};

export const courseService = {
  getCourses: async (schoolId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/courses?schoolId=${schoolId}`);
    if (!res.ok) throw new Error('Failed to fetch courses');
    return res.json();
  },
  
  createCourse: async (data) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/courses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to create course');
    return res.json();
  },
  
  updateCourse: async (id, data) => {
    console.log(`API call: updateCourse(${id})`, data);
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/courses/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error updating course: ${response.status}`, errorText);
        throw new Error(`Error updating course: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating course:', error);
      throw error;
    }
  },
  
  deleteCourse: async (courseId) => {
    const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/courses/${courseId}`, {
      method: 'DELETE'
    });
    if (!res.ok) throw new Error('Failed to delete course');
    return res.json();
  },
  
  getCourseTranslations: async (courseId) => {
    try {
      const token = getAccessToken();
      // Add timestamp parameter to prevent browser caching
      const timestamp = Date.now();
      const url = `${API_BASE_URL}/api/admin/courses/${courseId}/translations?_=${timestamp}`;
      
      console.log(`[CourseDebug] Fetching course translations from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        // Prevent browser from using cached responses
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Error fetching course translations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching course translations:', error);
      throw error;
    }
  },
  
  updateCourseTranslations: async (courseId, translations) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/courses/${courseId}/translations`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ translations }),
      });

      if (!response.ok) {
        throw new Error(`Error updating course translations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating course translations:', error);
      throw error;
    }
  }
};

export const schoolService = {
  // Get school by subdomain
  getSchool: async (subdomain, language = 'en') => {
    try {
      console.log(`[SchoolDebug] Getting school for subdomain: ${subdomain}`);
      const res = await fetch(`${API_BASE_URL}/api/schools/${subdomain}?language=${language}`);
      if (!res.ok) throw new Error(`Failed to fetch school: ${res.status}`);
      const data = await res.json();
      console.log(`[SchoolDebug] School data received:`, data);
      return data;
    } catch (error) {
      console.error('Error fetching school:', error);
      throw error;
    }
  },
  
  // Get public school settings including month navigation settings
  getPublicSchoolSettings: async (schoolId, language = 'en') => {
    try {
      console.log(`[SchoolDebug] Getting public school settings for ID: ${schoolId}`);
      const res = await fetch(`${API_BASE_URL}/api/schools/${schoolId}/settings?language=${language}`);
      if (!res.ok) throw new Error(`Failed to fetch school settings: ${res.status}`);
      const data = await res.json();
      console.log(`[SchoolDebug] School settings received:`, data);
      return data;
    } catch (error) {
      console.error('Error fetching school settings:', error);
      throw error;
    }
  },
  
  updateSchoolSettings: async (settings, schoolId) => {
    // Simple request deduplication to prevent duplicate requests
    const requestKey = `updateSchoolSettings_${schoolId}_${JSON.stringify(settings)}`;
    if (schoolService.updateSchoolSettings._pendingRequests && schoolService.updateSchoolSettings._pendingRequests.has(requestKey)) {
      console.log('[SchoolService] Duplicate request detected, waiting for existing request');
      return schoolService.updateSchoolSettings._pendingRequests.get(requestKey);
    }
    
    // Initialize pending requests map if not exists
    if (!schoolService.updateSchoolSettings._pendingRequests) {
      schoolService.updateSchoolSettings._pendingRequests = new Map();
    }
    
    try {
      // Create URL with query parameter for schoolId
      const params = new URLSearchParams();
      if (schoolId) {
        params.append('schoolId', schoolId.toString());
      }
      
      const url = `${API_BASE_URL}/api/admin/school-settings${params.toString() ? `?${params.toString()}` : ''}`;
      
      console.log('[SchoolService] Updating school settings with URL:', url);
      console.log('[SchoolService] Settings data:', settings);
      
      // Create the request promise
      const requestPromise = (async () => {
        try {
          const res = await fetchWithRefresh(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(settings)
          });
          
          if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to update school settings: ${res.status} ${res.statusText}`);
          }
          
          return await res.json();
        } finally {
          // Remove from pending requests when complete
          schoolService.updateSchoolSettings._pendingRequests.delete(requestKey);
        }
      })();
      
      // Store the request promise to prevent duplicates
      schoolService.updateSchoolSettings._pendingRequests.set(requestKey, requestPromise);
      
      return await requestPromise;
    } catch (error) {
      // Make sure to clean up pending request on error
      schoolService.updateSchoolSettings._pendingRequests.delete(requestKey);
      console.error('[SchoolService] Error updating school settings:', error);
      throw error;
    }
  },
  
  /**
   * Updates only the custom content for a school
   * @param {Object} customContent - Custom content by language
   * @param {number|string} schoolId - School ID
   * @returns {Promise<Object>} Updated school data
   */
  updateCustomContent: async (customContent, schoolId) => {
    try {
      // Create URL with query parameter for schoolId
      const params = new URLSearchParams();
      if (schoolId) {
        params.append('schoolId', schoolId.toString());
      }
      
      const url = `${API_BASE_URL}/api/admin/school-settings/custom-content${params.toString() ? `?${params.toString()}` : ''}`;
      
      const res = await fetchWithRefresh(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ custom_content: customContent })
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update custom content: ${res.status} ${res.statusText}`);
      }
      
      return await res.json();
    } catch (error) {
      console.error('Error updating custom content:', error);
      throw error;
    }
  },
  
  uploadLogo: async (file) => {
    // Create a FormData object
    const formData = new FormData();
    formData.append('logo', file);
    
    console.log(`Preparing to upload file: ${file.name}, size: ${file.size}, type: ${file.type}`);
    
    try {
      // Use fetch directly instead of fetchWithRefresh for simpler debugging
      const token = localStorage.getItem('accessToken');
      
      // Very basic fetch with minimal options
      const res = await fetch(`${API_BASE_URL}/api/admin/school-settings/logo`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
          // Explicitly NOT setting Content-Type so browser can set it correctly
        }
      });
      
      console.log(`Upload response status: ${res.status}`);
      
      if (!res.ok) {
        // eslint-disable-next-line no-unused-vars
        const text = await res.text();
        console.error(`Upload error: ${text}`);
        throw new Error(`Failed to upload logo: ${res.status} ${text}`);
      }
      
      // For successful responses, try to parse as JSON but have a fallback for text
      try {
        const data = await res.json();
        return data;
      } catch (jsonError) {
        console.log('Response not JSON, using text instead:', jsonError);
        // eslint-disable-next-line no-unused-vars
        const text = await res.text();
        // Still return a valid object with the needed fields
        return { 
          success: true, 
          message: 'Logo uploaded successfully', 
          logo_path: '/uploads/logos/placeholder.png' // fallback path
        };
      }
    } catch (error) {
      console.error('Error in uploadLogo:', error);
      throw error;
    }
  },
  
  updateSchoolSubdomain: async (subdomain, schoolId) => {
    try {
      console.log(`[API] CRITICAL SUBDOMAIN UPDATE: ${subdomain} for schoolId: ${schoolId}`);
      
      // Create URL with query parameter for schoolId
      const params = new URLSearchParams();
      if (schoolId) {
        params.append('schoolId', schoolId.toString());
      }
      
      // Add timestamp for cache busting
      params.append('_t', Date.now().toString());
      
      const url = `${API_BASE_URL}/api/admin/school-settings/subdomain${params.toString() ? `?${params.toString()}` : ''}`;
      console.log(`[API] Subdomain update URL: ${url}`);
      
      // Get token directly instead of using fetchWithRefresh to have more control
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.error('[API] Missing access token in updateSchoolSubdomain');
        throw new Error('Authentication required');
      }
      
      console.log(`[API] Sending subdomain update request with token: ${token.substring(0, 15)}...`);
      
      // Create request payload
      const payload = { subdomain };
      console.log(`[API] Subdomain update payload:`, payload);
      
      // Make the API call with retries
      let retryCount = 0;
      const maxRetries = 3;
      let response;
      
      while (retryCount < maxRetries) {
        try {
          console.log(`[API] Making subdomain update request (attempt ${retryCount + 1}/${maxRetries})`);
          
          response = await fetch(url, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            },
            cache: 'no-store',
            body: JSON.stringify(payload)
          });
          
          console.log(`[API] Subdomain update response status: ${response.status}`);
          
          // Break out of retry loop if successful
          if (response.ok) {
            break;
          }
          
          // If we get here, the request failed but we might retry
          console.warn(`[API] Subdomain update failed with status ${response.status}, attempt ${retryCount + 1}/${maxRetries}`);
          
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, retryCount)));
          retryCount++;
          
        } catch (fetchError) {
          console.error(`[API] Network error in subdomain update (attempt ${retryCount + 1}/${maxRetries}):`, fetchError);
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, retryCount)));
          retryCount++;
          
          // If we've tried the maximum number of times, rethrow the error
          if (retryCount >= maxRetries) {
            throw fetchError;
          }
        }
      }
      
      // Handle non-ok response after all retries
      if (!response.ok) {
        let errorMessage = `Failed to update subdomain: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          console.error(`[API] Subdomain update error:`, errorData);
        } catch (parseError) {
          console.error(`[API] Error parsing subdomain update error response:`, parseError);
        }
        throw new Error(errorMessage);
      }
      
      // Parse the successful response
      const text = await response.text();
      console.log(`[API] Subdomain update raw response:`, text);
      
      let result;
      try {
        result = text ? JSON.parse(text) : { success: true };
      } catch (parseError) {
        console.warn(`[API] Error parsing subdomain update success response, using default:`, parseError);
        result = { 
          success: true,
          subdomain,
          message: 'Subdomain updated successfully (response parsing failed)'
        };
      }
      
      console.log(`[API] Subdomain update success:`, result);
      return result;
    } catch (error) {
      console.error('[API] Fatal error updating subdomain:', error);
      throw error;
    }
  },
  
  /**
   * Checks if a subdomain is available
   * @param {string} subdomain - The subdomain to check
   * @param {number|string} schoolId - The school ID or 'new' for new schools
   * @returns {Promise<Object>} - Object with available status and message
   */
  checkSubdomainAvailability: async (subdomain, schoolId) => {
    try {
      // Ensure subdomain is provided
      if (!subdomain) {
        console.error('[API] Missing subdomain parameter in checkSubdomainAvailability');
        return {
          available: false,
          message: 'Subdomain is required'
        };
      }
      
      // Determine which endpoint to use based on schoolId
      let endpoint;
      let params = new URLSearchParams();
      
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      params.append('_t', timestamp.toString());
      
      if (schoolId === 'new') {
        // Use the public endpoint for new schools
        endpoint = `${API_BASE_URL}/api/schools/check-subdomain/${subdomain}`;
        console.log(`[API] Checking subdomain availability for new school: ${subdomain}, using public endpoint`);
      } else {
        // Use the admin endpoint for existing schools
        endpoint = `${API_BASE_URL}/api/admin/check-subdomain/${subdomain}`;
        
        // Ensure schoolId is a string
        if (typeof schoolId !== 'string') {
          schoolId = schoolId.toString();
        }
        
        params.append('schoolId', schoolId);
        console.log(`[API] Checking subdomain availability for: ${subdomain}, schoolId: ${schoolId}`);
      }
      
      const url = `${endpoint}${params.toString() ? `?${params.toString()}` : ''}`;
      console.log(`[API] Full URL: ${url}`);
      
      // Determine if we need authentication based on endpoint
      const headers = {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      };
      
      // Add auth token for admin endpoints only
      if (schoolId !== 'new') {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          console.error('[API] Missing access token in checkSubdomainAvailability');
          throw new Error('Authentication required');
        }
        headers['Authorization'] = `Bearer ${token}`;
        console.log(`[API] Sending authenticated request with token: ${token.substring(0, 15)}...`);
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers,
        cache: 'no-store' // Force fetching a fresh response
      });
      
      console.log(`[API] Subdomain check response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[API] Subdomain check failed: ${response.status}, Response: ${errorText}`);
        throw new Error(response.statusText || 'Failed to check subdomain');
      }
      
      const result = await response.json();
      console.log(`[API] Subdomain check result:`, result);
      return result;
    } catch (error) {
      console.error('[API] Error checking subdomain availability:', error);
      // Return a user-friendly error rather than throwing
      return {
        available: false,
        message: 'Could not verify subdomain availability'
      };
    }
  },

  uploadSchoolLogo: async (file, schoolId, isTemporary = false) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('isTemporary', isTemporary.toString());
      
      // Create URL with query parameter for schoolId
      const params = new URLSearchParams();
      if (schoolId) {
        params.append('schoolId', schoolId.toString());
      }
      
      const url = `${API_BASE_URL}/api/admin/school-settings/logo${params.toString() ? `?${params.toString()}` : ''}`;
      
      const res = await fetchWithRefresh(url, {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type for multipart/form-data, the browser will set it with the boundary
        }
      });
      
      if (!res.ok) {
        throw new Error(`Failed to upload logo: ${res.status} ${res.statusText}`);
      }
      
      return await res.json();
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  }
};

// Menu Types API
export const getMenuTypes = async (schoolId, language = 'en') => {
  try {
    const token = getAccessToken();
    // Add a timestamp to prevent browser caching
    const timestamp = Date.now();
    const url = `${API_BASE_URL}/api/admin/menu-types?schoolId=${schoolId}&language=${language}&_=${timestamp}`;
    
    console.log('Fetching menu types API call:', { 
      url, 
      schoolId, 
      language,
      tokenExists: !!token
    });
    
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      // Prevent the browser from using cached responses
      cache: 'no-store'
    });

    console.log('Menu types API response status:', response.status);
    
    // Special handling for 403 errors - just return empty array instead of throwing
    if (response.status === 403) {
      console.warn('Menu types API returned 403, returning empty array');
      return [];
    }
    
    if (!response.ok) {
      console.error('Menu types API error:', response.status, response.statusText);
      throw new Error(`Error fetching menu types: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Menu types API response data length:', data?.length || 0);
    return data || [];
  } catch (error) {
    console.error('Error fetching menu types:', error);
    throw error;
  }
};

export const updateMenuType = async (id, translations = {}, is_visible, show_nutrients, show_tags) => {
  try {
    const token = getAccessToken();
    const payload = { translations };
    
    // Only include is_visible if it's explicitly provided (could be false)
    if (is_visible !== undefined) {
      payload.is_visible = is_visible;
    }
    
    // Only include show_nutrients if it's explicitly provided (could be false)
    if (show_nutrients !== undefined) {
      payload.show_nutrients = show_nutrients;
    }
    
    // Only include show_tags if it's explicitly provided (could be false)
    if (show_tags !== undefined) {
      payload.show_tags = show_tags;
    }
    
    const response = await fetch(`${API_BASE_URL}/api/admin/menu-types/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Error updating menu type: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating menu type:', error);
    throw error;
  }
};

export const deleteMenuType = async (id) => {
  const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-types/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Failed to delete menu type');
  
  // Handle 204 No Content response (successful deletion)
  if (res.status === 204) {
    return { success: true };
  }

  // Only try to parse JSON if there's content
  const text = await res.text();
  if (text) {
    return JSON.parse(text);
  }
  
  return { success: true };
};

export const updateMenuTypeTranslation = async (menuTypeId, languageCode, translation) => {
  const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/translations/${languageCode}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ translation }),
  });
  if (!res.ok) throw new Error('Failed to update menu type translation');
  return res.json();
};

// Courses API
export const getCourses = async (schoolId, language = 'en') => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/courses?schoolId=${schoolId}&language=${language}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching courses: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching courses:', error);
    throw error;
  }
};

export const createCourse = async (schoolId, name, translations = {}) => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/courses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ schoolId, name, translations }),
    });

    if (!response.ok) {
      throw new Error(`Error creating course: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating course:', error);
    throw error;
  }
};

export const updateCourse = async (id, data) => {
  console.log(`API call: updateCourse(${id})`, data);
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/courses/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error updating course: ${response.status}`, errorText);
      throw new Error(`Error updating course: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating course:', error);
    throw error;
  }
};

export const deleteCourse = async (id) => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/courses/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error deleting course: ${response.statusText}`);
    }

    // Handle 204 No Content response (successful deletion)
    if (response.status === 204) {
      return { success: true };
    }

    // Only try to parse JSON if there's content
    const text = await response.text();
    if (text) {
      return JSON.parse(text);
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting course:', error);
    throw error;
  }
};

export const getCourseTranslations = async (courseId) => {
  try {
    const token = getAccessToken();
    // Add timestamp parameter to prevent browser caching
    const timestamp = Date.now();
    const url = `${API_BASE_URL}/api/admin/courses/${courseId}/translations?_=${timestamp}`;
    
    console.log(`[CourseDebug] Fetching course translations from: ${url}`);
    
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      // Prevent browser from using cached responses
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(`Error fetching course translations: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching course translations:', error);
    throw error;
  }
};

export const updateCourseTranslations = async (courseId, translations) => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/courses/${courseId}/translations`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ translations }),
    });

    if (!response.ok) {
      throw new Error(`Error updating course translations: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating course translations:', error);
    throw error;
  }
};

// Languages API
export const getLanguages = async () => {
  try {
    // Try the standard /api/languages endpoint first
    const res = await fetch(`${API_BASE_URL}/api/languages`);
    if (res.ok) {
      const data = await res.json();
      console.log('Languages response from standard endpoint:', data);
      return data;
    }
    console.warn('Standard languages endpoint failed, trying languages-direct');
    
    // Fallback to the direct endpoint
    const directRes = await fetch(`${API_BASE_URL}/api/languages-direct`);
    if (!directRes.ok) throw new Error('Failed to fetch languages from both endpoints');
    const directData = await directRes.json();
    console.log('Languages response from direct endpoint:', directData);
    return directData;
  } catch (error) {
    console.error('Error fetching languages:', error);
    throw error;
  }
};

// Custom Content API
export const getCustomContentTranslations = async (contentKey) => {
  try {
    // Get custom content directly from the school settings
    const settings = await schoolService.getSchoolSettings();
    return settings.customContent || {};
  } catch (error) {
    console.error('Error fetching custom content:', error);
    throw new Error('Failed to fetch custom content translations');
  }
};

export const updateCustomContentTranslation = async (contentKey, languageCode, translation) => {
  try {
    // Get current school settings
    const settings = await schoolService.getSchoolSettings();
    
    // Create updated content object - handle both potential property names
    const customContentObj = settings.customContent || settings.custom_content || {};
    customContentObj[languageCode] = translation;
    
    // Log for debugging
    console.log('Updating custom content:', customContentObj);
    
    // Update school settings with the new custom content
    // Use snake_case property name (custom_content) to match what the backend expects
    const updated = await schoolService.updateSchoolSettings({
      ...settings,
      custom_content: customContentObj
    });
    
    return updated;
  } catch (error) {
    console.error('Error updating custom content:', error);
    throw new Error('Failed to update custom content translation');
  }
};

export const updateCourseTranslation = async (courseId, languageCode, translation) => {
  const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/courses/${courseId}/translations/${languageCode}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ translation }),
  });
  if (!res.ok) throw new Error('Failed to update course translation');
  return res.json();
};

export const getSelectedLanguages = async (schoolId) => {
  try {
    // Ensure schoolId is provided and is a string
    if (schoolId !== undefined && schoolId !== null && typeof schoolId !== 'string') {
      schoolId = schoolId.toString();
    }
    
    const params = new URLSearchParams();
    if (schoolId) {
      params.append('schoolId', schoolId);
    }
    
    const url = `${API_BASE_URL}/api/admin/selected-languages${params.toString() ? `?${params.toString()}` : ''}`;
    
    const response = await fetchWithRefresh(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch selected languages: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching selected languages:', error);
    throw error;
  }
};

export const updateSelectedLanguages = async (languageIds, schoolId) => {
  try {
    // Ensure schoolId is provided and is a string
    if (schoolId !== undefined && schoolId !== null && typeof schoolId !== 'string') {
      schoolId = schoolId.toString();
    }
    
    const params = new URLSearchParams();
    if (schoolId) {
      params.append('schoolId', schoolId);
    }
    
    const url = `${API_BASE_URL}/api/admin/selected-languages${params.toString() ? `?${params.toString()}` : ''}`;
    
    const response = await fetchWithRefresh(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ languageIds })
    });

    if (!response.ok) {
      throw new Error(`Failed to update selected languages: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating selected languages:', error);
    throw error;
  }
};

/**
 * Check if a menu type is being used in menus
 * @param {number} id - Menu type ID
 * @returns {Promise<{count: number}>} Number of menus using the menu type
 */
export const getMenuTypeUsage = async (id) => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/menu-types/${id}/usage`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error checking menu type usage: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking menu type usage:', error);
    throw error;
  }
};

/**
 * Check if a course is being used in menu items
 * @param {number} id - Course ID
 * @returns {Promise<{count: number}>} Number of menu items using the course
 */
export const getCourseUsage = async (id) => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/courses/${id}/usage`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error checking course usage: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking course usage:', error);
    throw error;
  }
};

/**
 * Get courses for a menu type
 * @param {number} menuTypeId - Menu type ID
 * @param {string} language - Language code
 * @returns {Promise<Array>} Courses for the menu type
 */
export const getMenuTypeCourses = async (menuTypeId, language = 'en') => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/courses?language=${language}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching menu type courses: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching menu type courses:', error);
    throw error;
  }
};

/**
 * Get available courses for a menu type (not already linked)
 * @param {number} menuTypeId - Menu type ID
 * @param {string} language - Language code
 * @returns {Promise<Array>} Available courses for the menu type
 */
export const getAvailableCoursesForMenuType = async (menuTypeId, language = 'en', timestamp = null) => {
  try {
    const token = getAccessToken();
    // Add timestamp parameter to prevent browser caching
    const cacheBuster = timestamp ? `&_=${timestamp}` : `&_=${Date.now()}`;
    const url = `${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/available-courses?language=${language}${cacheBuster}`;
    
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      // Prevent browser from using cached responses
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(`Error fetching available courses for menu type: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching available courses for menu type:', error);
    throw error;
  }
};

/**
 * Add a course to a menu type
 * @param {number} menuTypeId - Menu type ID
 * @param {number} courseId - Course ID
 * @returns {Promise<Object>} Result of the operation
 */
export const addCourseToMenuType = async (menuTypeId, courseId) => {
  console.log(`API: Adding course ${courseId} to menu type ${menuTypeId}`);
  const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/courses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ courseId })
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to add course to menu type');
  }
  return res.json();
};

/**
 * Remove a course from a menu type
 * @param {number} menuTypeId - Menu type ID
 * @param {number} courseId - Course ID
 * @returns {Promise<Object>} Result of the operation
 */
export const removeCourseFromMenuType = async (menuTypeId, courseId) => {
  console.log(`API: Removing course ${courseId} from menu type ${menuTypeId}`);
  const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/courses/${courseId}`, {
    method: 'DELETE'
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to remove course from menu type');
  }
  
  // Handle 204 No Content response (successful deletion)
  if (res.status === 204) {
    return { success: true };
  }

  // Only try to parse JSON if there's content
  const text = await res.text();
  if (text) {
    return JSON.parse(text);
  }
  
  return { success: true };
};

/**
 * Reorder courses within a menu type
 * @param {number} menuTypeId - Menu type ID
 * @param {number} courseId - Course ID to reorder
 * @param {string} direction - Direction to move the course ('up' or 'down')
 * @returns {Promise<Object>} Result of the operation
 */
export const reorderMenuTypeCourse = async (menuTypeId, courseId, direction) => {
  console.log(`API: Reordering course ${courseId} in menu type ${menuTypeId}, direction: ${direction}`);
  const res = await fetchWithRefresh(`${API_BASE_URL}/api/admin/menu-types/${menuTypeId}/courses/${courseId}/reorder`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ direction })
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Failed to reorder course');
  }
  return res.json();
};

/**
 * Translation service for handling various translation operations
 */
export const translationService = {
  /**
   * Get pending translations
   * @returns {Promise<Array>} Pending translations
   */
  getPendingTranslations: async () => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/translations/pending`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error fetching pending translations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching pending translations:', error);
      throw error;
    }
  },

  /**
   * Get translations by type
   * @param {string} type - Type of item to get translations for (dish, specialName, etc.)
   * @returns {Promise<Array>} Translations for the specified type
   */
  getTranslationsByType: async (type) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/translations/${type}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error fetching translations for ${type}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching translations for ${type}:`, error);
      throw error;
    }
  },

  /**
   * Save a translation
   * @param {string} type - Type of item (dish, specialName, etc.)
   * @param {number} itemId - ID of the item
   * @param {string} languageCode - Language code
   * @param {string} translation - Translation text
   * @returns {Promise<Object>} Result of the operation
   */
  saveTranslation: async (type, itemId, languageCode, translation) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/translations/${type}/${itemId}/${languageCode}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ translation })
      });

      if (!response.ok) {
        throw new Error(`Error saving translation: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving translation:', error);
      throw error;
    }
  },

  /**
   * Export translations to a CSV file
   * @param {string} schoolId - School ID
   * @returns {Promise<{blob: Blob, filename: string}>} File data as a blob and filename
   */
  exportTranslations: async (schoolId) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/translations/export?schoolId=${schoolId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error exporting translations: ${response.statusText}`);
      }

      // Get the filename from the Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : 'translations.xlsx';

      // Get the blob from the response
      const blob = await response.blob();

      return {
        blob,
        filename
      };
    } catch (error) {
      console.error('Error exporting translations:', error);
      throw error;
    }
  },

  /**
   * Export all translations to a CSV file (not just missing translations)
   * @param {string} schoolId - School ID
   * @returns {Promise<{blob: Blob, filename: string}>} File data as a blob and filename
   */
  exportAllTranslations: async (schoolId) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/admin/translations/export-all?schoolId=${schoolId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error exporting all translations: ${response.statusText}`);
      }

      // Get the filename from the Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : 'all-translations.xlsx';

      // Get the blob from the response
      const blob = await response.blob();

      return {
        blob,
        filename
      };
    } catch (error) {
      console.error('Error exporting all translations:', error);
      throw error;
    }
  },

  /**
   * Validate a translation file
   * @param {File} file - The uploaded file
   * @param {number} schoolId - School ID
   * @returns {Promise<Object>} Validation results
   */
  validateTranslationFile: async (file, schoolId) => {
    try {
      const token = getAccessToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schoolId', schoolId);

      const response = await fetch(`${API_BASE_URL}/api/admin/translations/validate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error validating file: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error validating translation file:', error);
      throw error;
    }
  },

  /**
   * Import validated translations
   * @param {string} validationId - ID from the validation step
   * @param {number} schoolId - School ID
   * @param {string} selectedCodesString - Comma-separated string of selected language codes
   * @param {string} userLanguage - The current UI language
   * @returns {Promise<Object>} Import results (potentially including updatedMissingItems)
   */
  importTranslations: async (validationId, schoolId, selectedCodesString, userLanguage) => {
    try {
      const token = getAccessToken();
      
      // Prepare the body with all necessary data
      const body = {
        validationId,
        schoolId,
        selectedLanguageCodes: selectedCodesString, // Add selected codes
        userLanguage // Add user language
      };
      
      const response = await fetch(`${API_BASE_URL}/api/admin/translations/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body) // Send the enhanced body
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Error importing translations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error importing translations:', error);
      throw error;
    }
  },

  /**
   * Export ONLY items with missing translations for SELECTED languages.
   * @param {string} schoolId - School ID
   * @param {Array<string>} selectedLanguageCodes - Array of language codes (e.g., ['en', 'es'])
   * @returns {Promise<{blob: Blob, filename: string}>} File data as a blob and filename
   */
  exportSelectedTranslations: async (schoolId, selectedLanguageCodes) => {
    try {
      const token = getAccessToken();
      const params = new URLSearchParams({
        schoolId: schoolId.toString(),
        selectedLanguageCodes: selectedLanguageCodes.join(',')
      });
      const url = `${API_BASE_URL}/api/admin/translations/export-selected?${params.toString()}`;
      
      console.log(`[API] Exporting selected translations from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error exporting selected translations: ${response.statusText}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : 'selected-translations.xlsx';
      const blob = await response.blob();
      return { blob, filename };
    } catch (error) {
      console.error('Error exporting selected translations:', error);
      throw error;
    }
  },
  
  /**
   * Export ALL items (dishes and special names) with columns ONLY for SELECTED languages.
   * @param {string} schoolId - School ID
   * @param {Array<string>} selectedLanguageCodes - Array of language codes (e.g., ['en', 'es'])
   * @returns {Promise<{blob: Blob, filename: string}>} File data as a blob and filename
   */
  exportAllSelectedTranslations: async (schoolId, selectedLanguageCodes) => {
    try {
      const token = getAccessToken();
      const params = new URLSearchParams({
        schoolId: schoolId.toString(),
        selectedLanguageCodes: selectedLanguageCodes.join(',')
      });
      const url = `${API_BASE_URL}/api/admin/translations/export-all-selected?${params.toString()}`;
      
      console.log(`[API] Exporting all selected translations from: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error exporting all selected translations: ${response.statusText}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : 'all-selected-translations.xlsx';
      const blob = await response.blob();
      return { blob, filename };
    } catch (error) {
      console.error('Error exporting all selected translations:', error);
      throw error;
    }
  },
}

/**
 * Get users for a school
 * @param {number} schoolId - The school ID
 * @returns {Promise<Array>} List of users for the school
 */
export const getSchoolUsers = async (schoolId) => {
  try {
    // Add timestamp to prevent browser caching
    const timestamp = new Date().getTime();
    const response = await fetch(`${API_BASE_URL}/api/admin/users?schoolId=${schoolId}&t=${timestamp}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    if (!response.ok) {
      throw new Error(`Error fetching school users: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching school users:', error);
    throw error;
  }
};

/**
 * Invite an existing user to a school with specified roles
 * @param {number} schoolId - The school ID
 * @param {string} email - User's email
 * @param {Array<string>} roles - Roles to assign
 * @returns {Promise<Object>} Result of the invitation
 */
export const inviteUserToSchool = async (schoolId, email, roles) => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/users/invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ schoolId, email, roles })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error inviting user: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error inviting user to school:', error);
    throw error;
  }
};

/**
 * Update a user's roles for a specific school
 * @param {number} userId - The user ID
 * @param {number} schoolId - The school ID
 * @param {Array<string>} roles - New roles to assign
 * @returns {Promise<Object>} Result of the update
 */
export const updateUserRoles = async (userId, schoolId, roles) => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/roles`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ schoolId, roles })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error updating user roles: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating user roles:', error);
    throw error;
  }
};

/**
 * Remove a user from a school
 * @param {number} userId - The user ID
 * @param {number} schoolId - The school ID
 * @returns {Promise<Object>} Result of the removal
 */
export const removeUserFromSchool = async (userId, schoolId) => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/schools/${schoolId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error removing user from school: ${response.statusText}`);
    }

    // Handle 204 No Content response (successful deletion)
    if (response.status === 204) {
      return { success: true };
    }

    // Only try to parse JSON if there's content
    const text = await response.text();
    if (text) {
      return JSON.parse(text);
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error removing user from school:', error);
    throw error;
  }
};

/**
 * Check if a user exists by email
 * @param {string} email - User's email
 * @returns {Promise<Object>} Result with user existence information
 */
export const checkUserExists = async (email) => {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/api/admin/users/check?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error checking user: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error checking if user exists:', error);
    throw error;
  }
};

/**
 * User API functions
 */
export const userService = {
  // ... existing user service functions ...

  /**
   * Gets the current user's preferences
   * @returns {Promise<Object>} User preferences object
   */
  getUserPreferences: async () => {
    try {
      const response = await fetchWithRefresh('/api/user/preferences');
      if (!response.ok) {
        throw new Error(`Failed to get user preferences: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting user preferences:', error);
      throw error;
    }
  },

  /**
   * Updates the user's language preference
   * @param {string} language - The language code
   * @returns {Promise<Object>} Response data
   */
  updateLanguagePreference: async (language) => {
    try {
      const response = await fetchWithRefresh('/api/user/preferences/language', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ language })
      });
      if (!response.ok) {
        throw new Error(`Failed to update language preference: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating language preference:', error);
      throw error;
    }
  }
};

/**
 * Gets selected languages for a school - public endpoint version for unauthenticated users
 * @param {number|string} schoolId - The school ID
 * @returns {Promise<Array>} Selected languages for the school
 */
export const getPublicSelectedLanguages = async (schoolId) => {
  try {
    // Ensure schoolId is provided and is a string
    if (schoolId !== undefined && schoolId !== null && typeof schoolId !== 'string') {
      schoolId = schoolId.toString();
    }
    
    const params = new URLSearchParams();
    if (schoolId) {
      params.append('schoolId', schoolId);
    }
    
    // Use the public endpoint that doesn't require authentication
    const url = `${API_BASE_URL}/api/schools/${schoolId}/languages`;
    
    console.log(`[LanguageDebug] Fetching public selected languages for school ${schoolId} from ${url}`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      console.warn(`[LanguageDebug] Failed to fetch public selected languages: ${response.status} ${response.statusText}`);
      // Return all languages as fallback if we can't get school-specific ones
      console.log(`[LanguageDebug] Falling back to all languages for school ${schoolId}`);
      return await getLanguages();
    }

    const languages = await response.json();
    console.log(`[LanguageDebug] Received ${languages.length} selected languages for school ${schoolId}:`, 
      languages.map(l => `${l.code} (${l.id})`));
    
    return languages;
  } catch (error) {
    console.error('[LanguageDebug] Error fetching public selected languages:', error);
    // Return all languages as fallback
    console.log(`[LanguageDebug] Falling back to all languages due to error for school ${schoolId}`);
    return await getLanguages();
  }
};

// Add the new function for fetching unified items
export const getUnifiedItems = async (schoolId, itemType, uiLanguage, selectedLanguageCodes) => {
  try {
    const token = getAccessToken();
    const params = new URLSearchParams({
      schoolId: schoolId.toString(),
      itemType,
      uiLanguage,
      selectedLanguageCodes: selectedLanguageCodes.join(','), // Join array into comma-separated string
      _t: Date.now().toString() // Cache buster
    });
    
    const url = `${API_BASE_URL}/api/admin/unified-items?${params.toString()}`;
    console.log(`[API] Fetching unified items from: ${url}`);
    
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      cache: 'no-store'
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[API] Error fetching unified items (${response.status}): ${errorText}`);
      throw new Error(`Error fetching unified items: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[API] Error in getUnifiedItems:', error);
    throw error; // Re-throw to be caught by calling component/hook
  }
};

// Nutrient service object for managing nutrients
export const nutrientService = {
  // List all nutrients for a school (Admin)
  listNutrients: async (schoolId) => {
    if (!schoolId) {
      throw new Error('School ID is required');
    }
    
    // Add timestamp for cache-busting
    const timestamp = Date.now();
    // --- BEGIN CHANGE ---
    // Use the correct route /api/admin/nutrients with schoolId as query parameter
    const url = `${API_BASE_URL}/api/admin/nutrients?schoolId=${schoolId}&_t=${timestamp}`;
    // --- END CHANGE ---

    console.log(`[nutrientService] Fetching nutrients with timestamp: ${timestamp}`);
    
    try {
      const response = await fetchWithRefresh(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[nutrientService] Error response: ${errorText}`);
        throw new Error(`Error fetching nutrients: ${response.status}`);
      }
      
      const nutrients = await response.json();
      console.log(`[nutrientService] Successfully retrieved ${nutrients.length} nutrients`);
      return nutrients;
    } catch (error) {
      console.error('[nutrientService] Error in listNutrients:', error);
      throw error;
    }
  },
  
  // Create a new nutrient (Admin)
  createNutrient: async (nutrient, schoolId) => {
    try {
      // --- BEGIN CHANGE ---
      // Correct the URL to /api/admin/nutrients
      const url = `${API_BASE_URL}/api/admin/nutrients`;
      // --- END CHANGE ---
      const res = await fetchWithRefresh(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        // --- BEGIN CHANGE ---
        // Ensure schoolId is part of the body as expected by the backend route
        body: JSON.stringify({ ...nutrient, schoolId })
        // --- END CHANGE ---
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to create nutrient: ${res.status} ${res.statusText}`);
      }
      
      return await res.json();
    } catch (error) {
      console.error('Error creating nutrient:', error);
      throw error;
    }
  },
  
  // Get a specific nutrient (Admin)
  getNutrient: async (nutrientId, schoolId) => {
    try {
      if (!nutrientId || !schoolId) {
        throw new Error('Nutrient ID and School ID are required');
      }
      
      // Add timestamp for cache-busting
      const timestamp = Date.now();
      // --- BEGIN CHANGE ---
      // Correct the URL to /api/admin/nutrients/:id and include schoolId as query for consistency/middleware check
      const url = `${API_BASE_URL}/api/admin/nutrients/${nutrientId}?schoolId=${schoolId}&_t=${timestamp}`;
      // --- END CHANGE ---

      console.log(`[nutrientService] Fetching nutrient ${nutrientId} with timestamp: ${timestamp}`);
      
      const res = await fetchWithRefresh(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to get nutrient: ${res.status} ${res.statusText}`);
      }
      
      return await res.json();
    } catch (error) {
      console.error('Error getting nutrient:', error);
      throw error;
    }
  },
  
  // Update an existing nutrient (Admin)
  updateNutrient: async (nutrientId, nutrient, schoolId) => {
    try {
      if (!nutrientId || !schoolId) {
        throw new Error('Nutrient ID and School ID are required');
      }
      
      const timestamp = Date.now();
      // --- BEGIN CHANGE ---
      // Correct the URL to /api/admin/nutrients/:id
      const url = `${API_BASE_URL}/api/admin/nutrients/${nutrientId}?_t=${timestamp}`;
      // --- END CHANGE ---

      console.log(`[nutrientService] Updating nutrient ${nutrientId} with timestamp: ${timestamp}`);
      
      const res = await fetchWithRefresh(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        // --- BEGIN CHANGE ---
        // Ensure schoolId is sent in the body for the middleware
        body: JSON.stringify({ ...nutrient, schoolId }),
        // --- END CHANGE ---
        cache: 'no-store'
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update nutrient: ${res.status} ${res.statusText}`);
      }
      
      return await res.json();
    } catch (error) {
      console.error('Error updating nutrient:', error);
      throw error;
    }
  },
  
  // Delete a nutrient (Admin)
  deleteNutrient: async (nutrientId, schoolId) => {
    try {
      if (!nutrientId || !schoolId) {
        throw new Error('Nutrient ID and School ID are required');
      }
      
      const timestamp = Date.now();
      // --- BEGIN CHANGE ---
      // Correct the URL to /api/admin/nutrients/:id and include schoolId as query param
      const url = `${API_BASE_URL}/api/admin/nutrients/${nutrientId}?schoolId=${schoolId}&_t=${timestamp}`;
      // --- END CHANGE ---

      console.log(`[nutrientService] Deleting nutrient ${nutrientId} with timestamp: ${timestamp}`);
      
      const res = await fetchWithRefresh(url, {
        method: 'DELETE',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!res.ok && res.status !== 204) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete nutrient: ${res.status} ${res.statusText}`);
      }
      
      return res.status === 204 ? true : await res.json();
    } catch (error) {
      console.error('Error deleting nutrient:', error);
      throw error;
    }
  },
  
  // Get ONLY enabled nutrients for a school, with names translated to the specified language (Admin)
  // Includes fallback logic for translations.
  listEnabledNutrients: async (schoolId, languageCode = 'en', defaultLanguageCode = 'en') => { 
    if (!schoolId) {
      console.warn('[API] listEnabledNutrients called without schoolId');
      return []; // Return empty array if no schoolId
    }
    
    try {
      const timestamp = Date.now(); // Cache buster
      const params = new URLSearchParams({
        enabled: 'true',
        // Send the preferred language to the backend as a hint, though we'll handle fallback client-side
        lang: languageCode, 
        // --- BEGIN CHANGE ---
        // Pass schoolId as query param
        schoolId: schoolId,
        // --- END CHANGE ---
        _t: timestamp.toString()
      });
      // --- BEGIN CHANGE ---
      // Correct the base URL
      const url = `${API_BASE_URL}/api/admin/nutrients?${params.toString()}`;
      // --- END CHANGE ---
      
      console.log(`[API] Fetching enabled nutrients from: ${url}`);
      
      const response = await fetchWithRefresh(url, {
         headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
         const errorText = await response.text();
         console.error(`[API] Error fetching enabled nutrients (${response.status}): ${errorText}`);
         return []; 
      }
      
      // Process the raw response (assuming format like user sample: [{ id, ..., translations: {...} }, ...])
      const rawNutrients = await response.json();
      console.log(`[API] Raw enabled nutrients fetched:`, rawNutrients);
      
      const processedNutrients = rawNutrients.map(nutrient => {
        const translations = nutrient.translations || {};
        let name = null;
        let isFallback = false;
        let fallbackLanguage = null;

        // 1. Try UI language
        if (translations[languageCode]) {
          name = translations[languageCode];
        } 
        // 2. Try Default language
        else if (translations[defaultLanguageCode]) {
          name = translations[defaultLanguageCode];
          isFallback = true;
          fallbackLanguage = defaultLanguageCode.toUpperCase();
        } 
        // 3. Try any other available translation
        else {
          const availableLangCode = Object.keys(translations).find(code => translations[code]);
          if (availableLangCode) {
            name = translations[availableLangCode];
            isFallback = true;
            fallbackLanguage = availableLangCode.toUpperCase();
          }
        }

        // 4. Absolute fallback
        if (!name) {
          name = `Nutrient ${nutrient.id}`; // Fallback name
          isFallback = true; 
          fallbackLanguage = null; // No specific language source
        }
        
        return {
          id: nutrient.id,
          name: name, // The resolved name
          isFallback: isFallback,
          fallbackLanguage: fallbackLanguage,
          isEnabled: nutrient.is_enabled, // Include the enabled status
          unit: nutrient.unit || null // <<< ADDED THIS LINE (use null if undefined)
        };
      });

      console.log(`[API] Processed nutrients:`, processedNutrients);
      return processedNutrients;
      
    } catch (error) {
      console.error('[API] Error in listEnabledNutrients:', error);
      return []; // Return empty on exception
    }
  },
  
  // Get ALL nutrients for a school (both enabled and disabled), with names translated 
  // Includes fallback logic for translations.
  listAllNutrients: async (schoolId, languageCode = 'en', defaultLanguageCode = 'en') => { 
    if (!schoolId) {
      console.warn('[API] listAllNutrients called without schoolId');
      return []; // Return empty array if no schoolId
    }
    
    try {
      const timestamp = Date.now(); // Cache buster
      const params = new URLSearchParams({
        // Removed enabled=true filter to get all nutrients
        lang: languageCode, 
        // --- BEGIN CHANGE ---
        // Pass schoolId as query param
        schoolId: schoolId,
        // --- END CHANGE ---
        _t: timestamp.toString()
      });
      // --- BEGIN CHANGE ---
      // Correct the base URL
      const url = `${API_BASE_URL}/api/admin/nutrients?${params.toString()}`;
      // --- END CHANGE ---
      
      console.log(`[API] Fetching all nutrients from: ${url}`);
      
      const response = await fetchWithRefresh(url, {
         headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
         const errorText = await response.text();
         console.error(`[API] Error fetching nutrients (${response.status}): ${errorText}`);
         return []; 
      }
      
      // Process the raw response
      const rawNutrients = await response.json();
      console.log(`[API] Raw nutrients fetched:`, rawNutrients);
      
      const processedNutrients = rawNutrients.map(nutrient => {
        const translations = nutrient.translations || {};
        let name = null;
        let isFallback = false;
        let fallbackLanguage = null;

        // 1. Try UI language
        if (translations[languageCode]) {
          name = translations[languageCode];
        } 
        // 2. Try Default language
        else if (translations[defaultLanguageCode]) {
          name = translations[defaultLanguageCode];
          isFallback = true;
          fallbackLanguage = defaultLanguageCode.toUpperCase();
        } 
        // 3. Try any other available translation
        else {
          const availableLangCode = Object.keys(translations).find(code => translations[code]);
          if (availableLangCode) {
            name = translations[availableLangCode];
            isFallback = true;
            fallbackLanguage = availableLangCode.toUpperCase();
          }
        }

        // 4. Absolute fallback
        if (!name) {
          name = `Nutrient ${nutrient.id}`; // Fallback name
          isFallback = true; 
          fallbackLanguage = null; // No specific language source
        }
        
        return {
          id: nutrient.id,
          name: name, // The resolved name
          isFallback: isFallback,
          fallbackLanguage: fallbackLanguage,
          isEnabled: nutrient.is_enabled, // Include the enabled status
          unit: nutrient.unit || null // <<< ADDED THIS LINE (use null if undefined)
        };
      });

      console.log(`[API] Processed nutrients:`, processedNutrients);
      return processedNutrients;
      
    } catch (error) {
      console.error('[API] Error in listAllNutrients:', error);
      return []; // Return empty on exception
    }
  },
  
  // Keeping the original method for backward compatibility, just calling the new function
  listEnabledNutrients: async (schoolId, languageCode = 'en', defaultLanguageCode = 'en') => {
    const allNutrients = await nutrientService.listAllNutrients(schoolId, languageCode, defaultLanguageCode);
    // Filter for only enabled nutrients
    return allNutrients.filter(nutrient => nutrient.isEnabled);
  },
};

// NEW: Nutrient Queue Service
export const nutrientQueueService = {
  /**
   * Gets dishes missing nutrient values for a school.
   * @param {number|string} schoolId - The school ID.
   * @returns {Promise<Array>} List of dishes with missing nutrient info.
   */
  getMissingNutrients: async (schoolId) => {
    if (!schoolId) {
      console.warn('[API][NutrientQueue] getMissingNutrients called without schoolId');
      return [];
    }
    try {
      const timestamp = Date.now(); // Cache buster
      const url = `${API_BASE_URL}/api/admin/schools/${schoolId}/nutrient-queue?_t=${timestamp}`;
      
      console.log(`[API][NutrientQueue] Fetching nutrient queue from: ${url}`);
      
      const response = await fetchWithRefresh(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[API][NutrientQueue] Error fetching nutrient queue (${response.status}): ${errorText}`);
        return []; // Return empty on error
      }
      
      const data = await response.json();
      console.log(`[API][NutrientQueue] Received ${data?.length ?? 0} items.`);
      return data;
    } catch (error) {
      console.error('[API][NutrientQueue] Error in getMissingNutrients:', error);
      return [];
    }
  },

  /**
   * Saves nutrient values for a specific dish.
   * @param {number|string} dishId - The ID of the dish.
   * @param {Object} nutrientValues - Object mapping nutrientId to value.
   * @param {number|string} schoolId - The school ID (for authorization context).
   * @returns {Promise<Object>} API response.
   */
  saveNutrientValues: async (dishId, nutrientValues, schoolId) => {
    if (!dishId || !nutrientValues || !schoolId) {
      throw new Error('dishId, nutrientValues, and schoolId are required to save nutrient values.');
    }
    try {
      const url = `${API_BASE_URL}/api/admin/nutrient-queue/save`;
      const payload = {
        itemId: dishId,
        itemType: 'dish', // Explicitly set type
        nutrientValues,
        schoolId
      };
      
      console.log(`[API][NutrientQueue] Saving nutrient values for dish ${dishId}`);
      
      const response = await fetchWithRefresh(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`[API][NutrientQueue] Error saving nutrient values (${response.status}):`, errorData);
        throw new Error(errorData.error || 'Failed to save nutrient values');
      }
      
      const data = await response.json();
      console.log(`[API][NutrientQueue] Nutrient values saved successfully for dish ${dishId}.`);
      return data;
    } catch (error) {
      console.error(`[API][NutrientQueue] Error in saveNutrientValues for dish ${dishId}:`, error);
      throw error;
    }
  }
};

export const translationBulkService = {
  // ... existing methods ...
};

// Export service for bulk nutrient operations
export const nutrientBulkService = {
  /**
   * Exports nutrient data as an Excel file.
   * @param {number} schoolId The ID of the school.
   * @param {string} type Export type ('missing' or 'all').
   * @param {string} [lang='en'] The UI language code.
   * @param {string} [refNameHeaderText='Reference Name'] Localized text for the reference name header.
   * @returns {Promise<{filename: string, blob: Blob}>} The filename and blob.
   */
  exportNutrients: async (schoolId, type = 'missing', lang = 'en', refNameHeaderText = 'Reference Name') => {
    // Construct URL with type, language, and header text parameters
    const params = new URLSearchParams({
      type: type,
      lang: lang,
      refNameHeader: refNameHeaderText, // Add the localized header text
      _t: Date.now().toString() // Add cache buster
    });
    const url = `${API_BASE_URL}/api/schools/${schoolId}/bulk-nutrients/export?${params.toString()}`;
    console.log(`[API][NutrientBulk] Exporting nutrients from ${url}`);
    try {
      const response = await fetchWithRefresh(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to export nutrients: ${response.status}`);
      }
      
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'nutrients_export.xlsx'; // Default filename
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      const blob = await response.blob();
      console.log(`[API][NutrientBulk] Nutrient export successful. Filename: ${filename}`);
      return { filename, blob };
    } catch (error) {
      console.error('[API][NutrientBulk] Error exporting nutrients:', error);
      throw error;
    }
  },

  /**
   * Validates an uploaded nutrient file.
   * @param {File} file The Excel file to validate.
   * @param {number} schoolId The ID of the school.
   * @param {string} [uiLanguage='en'] The language code used for the export.
   * @param {string} [refNameHeader='Reference Name'] The localized header text for the reference name column.
   * @returns {Promise<object>} The validation result { validationId, summary }.
   */
  validateNutrientFile: async (file, schoolId, uiLanguage = 'en', refNameHeader = 'Reference Name') => {
    // Construct URL with language and header text parameters
    const params = new URLSearchParams({
      lang: uiLanguage,
      refNameHeader: refNameHeader, 
      _t: Date.now().toString() // Cache buster
    });
    const url = `${API_BASE_URL}/api/schools/${schoolId}/bulk-nutrients/validate-import?${params.toString()}`;
    
    console.log(`[API][NutrientBulk] Validating nutrient file for school ${schoolId} (${file.name}) using lang=${uiLanguage}, refNameHeader="${refNameHeader}"`);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetchWithRefresh(url, {
        method: 'POST',
        body: formData,
        // No Content-Type header needed for FormData, browser sets it correctly
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || `Validation failed: ${response.status}`);
      }
      
      console.log('[API][NutrientBulk] Nutrient file validation result:', result);
      return result;
    } catch (error) {
      console.error('[API][NutrientBulk] Error validating nutrient file:', error);
      throw error;
    }
  },

  /**
   * Confirms the import of validated nutrient data.
   * @param {string} validationId The ID received from successful validation.
   * @param {number} schoolId The ID of the school.
   * @returns {Promise<object>} The import result { success, message, updatedMissingItems }.
   */
  importNutrients: async (validationId, schoolId) => {
    const url = `${API_BASE_URL}/api/schools/${schoolId}/bulk-nutrients/confirm-import`;
    console.log(`[API][NutrientBulk] Confirming nutrient import for school ${schoolId} with validation ID: ${validationId}`);

    try {
      const response = await fetchWithRefresh(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ validationId }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || `Import failed: ${response.status}`);
      }

      console.log('[API][NutrientBulk] Nutrient import confirmed successfully:', result);
      return result;
    } catch (error) {
      console.error('[API][NutrientBulk] Error confirming nutrient import:', error);
      throw error;
    }
  },
};

export default {
  // ... existing exports ...
  menuService,
  dishService,
  specialNameService,
  courseService,
  schoolService,
  nutrientService, // Make sure nutrientService is exported
  nutrientQueueService, // Export the new service
  translationService,
  getLanguages,
  getMenuTypes,
  updateMenuType,
  deleteMenuType,
  updateMenuTypeTranslation,
  getCourseUsage,
  getMenuTypeUsage,
  getMenuTypeCourses,
  getAvailableCoursesForMenuType,
  addCourseToMenuType,
  removeCourseFromMenuType,
  reorderMenuTypeCourse,
  getSchoolUsers,
  inviteUserToSchool,
  updateUserRoles,
  removeUserFromSchool,
  checkUserExists,
  getSelectedLanguages,
  updateSelectedLanguages,
  getPublicSelectedLanguages, // Ensure this is exported if used elsewhere
  getUnifiedItems, // Ensure this is exported
};

// Define the tag service for managing tags
export const tagService = {
  // List all tags for a school
  getTags: async (schoolId, language) => {
    if (!schoolId) {
      throw new Error('School ID is required');
    }
    
    // Add timestamp for cache-busting
    const timestamp = Date.now();
    let url = `${API_BASE_URL}/api/admin/tags?schoolId=${schoolId}&_t=${timestamp}`;
    
    if (language) {
      url += `&language=${language}`;
    }
    
    console.log(`[tagService] Fetching tags with timestamp: ${timestamp}`);
    
    try {
      const response = await fetchWithRefresh(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[tagService] Error response: ${errorText}`);
        throw new Error(`Error fetching tags: ${response.status}`);
      }
      
      const tags = await response.json();
      console.log(`[tagService] Successfully retrieved ${tags.length} tags`);
      return tags;
    } catch (error) {
      console.error('[tagService] Error in getTags:', error);
      throw error;
    }
  },
  
  // Get dishes needing tag information for queue
  getDishesNeedingTags: async (schoolId, language, search = '') => {
    try {
      if (!schoolId) {
        throw new Error('School ID is required');
      }
      
      const timestamp = Date.now();
      let url = `${API_BASE_URL}/api/admin/tags/pending?school_id=${schoolId}&_t=${timestamp}`;
      
      if (language) {
        url += `&language=${language}`;
      }
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      console.log(`[tagService] Fetching dishes needing tags with timestamp: ${timestamp}`);
      
      const response = await fetchWithRefresh(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[tagService] Error response: ${errorText}`);
        throw new Error(`Error fetching dishes needing tags: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`[tagService] Got response for dishes needing tags:`, data);
      
      // Extract dishes array from response - handle both array and object with dishes property
      const dishes = Array.isArray(data) ? data : (data.dishes || []);
      console.log(`[tagService] Successfully retrieved ${dishes.length} dishes needing tags`);
      return dishes;
    } catch (error) {
      console.error('[tagService] Error in getDishesNeedingTags:', error);
      throw error;
    }
  },
  
  // Get tags for a specific dish
  getDishTags: async (dishId, language) => {
    try {
      if (!dishId) {
        throw new Error('Dish ID is required');
      }
      
      const timestamp = Date.now();
      let url = `${API_BASE_URL}/api/admin/tags/dish/${dishId}?_t=${timestamp}`;
      
      // Add language parameter if provided
      if (language) {
        url += `&language=${language}`;
      }
      
      console.log(`[tagService] Fetching tags for dish ${dishId}`);
      
      const response = await fetchWithRefresh(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[tagService] Error response: ${errorText}`);
        throw new Error(`Error fetching dish tags: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`[tagService] Successfully retrieved tags for dish ${dishId}:`, 
        data.no_tags ? 'No tags' : `${data.tags.length} tags`);
      return data;
    } catch (error) {
      console.error(`[tagService] Error fetching tags for dish ${dishId}:`, error);
      throw error;
    }
  },
  
  // Update tags for a dish
  updateDishTags: async (dishId, tagIds, noTags) => {
    try {
      if (!dishId) {
        throw new Error('Dish ID is required');
      }
      
      const url = `${API_BASE_URL}/api/admin/tags/dish/${dishId}`;
      
      console.log(`[tagService] Updating tags for dish ${dishId}:`, 
        noTags ? 'No tags' : `${tagIds.length} tags`);
      
      const response = await fetchWithRefresh(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          no_tags: noTags,
          tag_ids: noTags ? [] : tagIds
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[tagService] Error response: ${errorText}`);
        throw new Error(`Failed to update dish tags: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`[tagService] Successfully updated tags for dish ${dishId}`);
      return data;
    } catch (error) {
      console.error(`[tagService] Error updating tags for dish ${dishId}:`, error);
      throw error;
    }
  },
  
  // Get a single tag with all translations
  getTag: async (tagId, schoolId) => {
    try {
      if (!tagId || !schoolId) {
        throw new Error('Tag ID and School ID are required');
      }
      
      const timestamp = Date.now();
      const url = `${API_BASE_URL}/api/admin/tags/${tagId}?schoolId=${schoolId}&include_translations=true&_t=${timestamp}`;
      
      console.log(`[tagService] Fetching tag ${tagId} with timestamp: ${timestamp}`);
      
      const response = await fetchWithRefresh(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[tagService] Error response: ${errorText}`);
        throw new Error(`Error fetching tag: ${response.status}`);
      }
      
      const tag = await response.json();
      console.log(`[tagService] Successfully retrieved tag ${tagId}`);
      return tag;
    } catch (error) {
      console.error(`[tagService] Error fetching tag ${tagId}:`, error);
      throw error;
    }
  },
  
  // Create a new tag
  createTag: async (schoolId, tag) => {
    try {
      if (!schoolId) {
        throw new Error('School ID is required');
      }
      
      const url = `${API_BASE_URL}/api/admin/tags`;
      
      console.log(`[tagService] Creating tag for school ${schoolId}`);
      console.log(`[tagService] Tag data received:`, tag);
      
      const requestBody = {
        schoolId,
        ...tag
      };
      
      console.log(`[tagService] Request body:`, requestBody);
      
      const response = await fetchWithRefresh(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[tagService] Error response: ${errorText}`);
        throw new Error(`Failed to create tag: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`[tagService] Successfully created tag with ID ${data.id}`);
      return data;
    } catch (error) {
      console.error('[tagService] Error creating tag:', error);
      throw error;
    }
  },
  
  // Update an existing tag
  updateTag: async (tagId, tag) => {
    try {
      if (!tagId) {
        throw new Error('Tag ID is required');
      }
      
      const url = `${API_BASE_URL}/api/admin/tags/${tagId}`;
      
      console.log(`[tagService] Updating tag ${tagId}`);
      
      const response = await fetchWithRefresh(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tag)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[tagService] Error response: ${errorText}`);
        throw new Error(`Failed to update tag: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`[tagService] Successfully updated tag ${tagId}`);
      return data;
    } catch (error) {
      console.error(`[tagService] Error updating tag ${tagId}:`, error);
      throw error;
    }
  },
  
  // Delete a tag
  deleteTag: async (tagId) => {
    try {
      if (!tagId) {
        throw new Error('Tag ID is required');
      }
      
      const url = `${API_BASE_URL}/api/admin/tags/${tagId}`;
      
      console.log(`[tagService] Deleting tag ${tagId}`);
      
      const response = await fetchWithRefresh(url, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[tagService] Error response: ${errorText}`);
        throw new Error(`Failed to delete tag: ${response.status}`);
      }
      
      console.log(`[tagService] Successfully deleted tag ${tagId}`);
      return { success: true };
    } catch (error) {
      console.error(`[tagService] Error deleting tag ${tagId}:`, error);
      throw error;
    }
  },
  
  // Update school's tag display setting
  updateTagSetting: async (schoolId, showTags) => {
    try {
      if (!schoolId) {
        throw new Error('School ID is required');
      }
      
      const url = `${API_BASE_URL}/api/admin/school-settings?schoolId=${schoolId}`;
      
      console.log(`[tagService] Updating tag setting for school ${schoolId}: showTags=${showTags}`);
      
      const response = await fetchWithRefresh(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          show_tags: showTags
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[tagService] Error response: ${errorText}`);
        throw new Error(`Failed to update tag setting: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`[tagService] Successfully updated tag setting for school ${schoolId}`);
      return data;
    } catch (error) {
      console.error(`[tagService] Error updating tag setting for school ${schoolId}:`, error);
      throw error;
    }
  }
};